import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    applyStrokeWidthKick,
    applyVelocityKick,
    clampPointInsideBounds,
    clampSpeed,
    clusterConsecutiveBySpawnGroupId,
    createBrushStamp,
    createTextStamp,
    pickNextBrushTextChar,
    resolveBrushTextInput,
    applySpiralTrailOffset,
    strokeDashArrayFromSettings,
    isOutsideOrOnEdge,
    parseViewBoxBounds,
    pointsToPathDWithStyle,
    MAX_TRAIL_LENGTH_CAP,
    computeChildTrailLengthCap,
    randomPointInDisk,
    randomVelocity,
    advancePeriodicSpawnTimer,
    sampleKickDelay,
    sampleTrailLengthCap,
    snapVelocityToDirectionLock,
    resolveAgentStrokeColor,
    stampsToSvgFragments,
    trailToSvgFragments,
    unitPerpendicularToMotion,
    stampPositionsAlongSegment,
    type AgentBrushMode,
    type BrushStampSpacingMode,
    type AgentStamp,
    type ChildTrailCapMode,
    type DirectionLockMode,
    type ViewBoxBounds,
} from "../../utils/pathAgents";
import {
    type AgentInputMode,
    DEFAULT_AGENT_INPUT_MODE,
} from "../../utils/agentInputMode";
import { buildFlowAgentsGroupUnion } from "../../utils/flowAgentsUnion";
import { collectSpawnSamplesFromSvg } from "../../utils/pathAgentsSpawnGuide";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";

export type {
    AgentBrushMode,
    AgentStamp,
    BrushStampSpacingMode,
    ChildTrailCapMode,
    DirectionLockMode,
};

const DEFAULT_PATH_AGENTS_PRESET: Record<string, unknown> = {
    kickIntervalSec: 1,
    kickIntervalRandomnessSec: 0,
    velocityKickGradual: false,
    velocityKickBlendFrames: 10,
    forceMagnitude: 80,
    baseSpeed: 120,
    maxSpeed: 400,
    directionLock: "none",
    maxTrailLength: 2400,
    maxTrailLengthRandomness: 0,
    stopAtMargin: true,
    spawnChildIntervalSec: 0,
    spawnChildIntervalRandomnessSec: 0,
    childTrailCapMode: "none",
    childTrailCapFixed: 2400,
    childTrailCapRandomness: 0,
    gravity: 0,
    strokeColor: "#e0e0e0",
    strokePaletteText: "",
    randomStrokeColorPerAgent: false,
    strokeWidth: 8,
    minStrokeWidth: 0.25,
    initialStrokeWidth: 1,
    strokeWidthStep: 0.5,
    strokeWidthKickFrames: 15,
    strokeWidthKickGradual: false,
    strokeWidthBlendFrames: 10,
    backgroundColor: "#1a1a1a",
    exportBackground: false,
    smoothPaths: false,
    mergeTrailToSinglePath: true,
    unionGroupOnComplete: true,
    filledVariableWidthStroke: false,
    trailTaperStartLength: 0,
    trailTaperEndLength: 0,
    dashedStroke: false,
    strokeDashLength: 16,
    strokeDashGap: 12,
    groupSize: 1,
    groupRadius: 80,
    strokeLayoutCount: 1,
    strokeLayoutSpacing: 12,
    groupCohesionStrength: 6,
    groupVariedBrushes: true,
    agentBrushMode: "stroke",
    brushStampIntervalFrames: 8,
    brushPolygonSides: 6,
    brushStampSizeScale: 1.5,
    brushStampSpacingMode: "frames",
    brushStampDistancePx: 24,
    brushTextContent: "",
    brushTextStrokeUnderlayColor: "#111111",
    brushTextStrokeUnderlayScale: 1.75,
    brushTextRandomStartOffset: false,
    exportGroupTextByAgent: false,
    spiralTrail: false,
    spiralAmplitude: 14,
    spiralWavelength: 80,
    spiralGrowthPer1k: 0.15,
    inputMode: DEFAULT_AGENT_INPUT_MODE,
};

/** Spring strength toward puppet brush target (leaders / solo agents; not group followers or parallel stroke slaves). */
const PUPPET_PULL_K = 14;

export type AddAgentAtOptions = {
    anchorLeaderAtPointer?: boolean;
};

export interface PathAgent {
    id: string;
    /** All agents from the same canvas click share this id (for grouping paths). */
    spawnGroupId: string;
    x: number;
    y: number;
    vx: number;
    vy: number;
    points: { x: number; y: number }[];
    /** Width at each vertex (same length as `points`). */
    strokeWidths: number[];
    /** Current width; starts at initial stroke width, updated on stroke-width kicks. */
    liveStrokeWidth: number;
    /** Stroke / stamp / text fill color for this agent (from palette when random is on). */
    strokeColor: string;
    /** Followers steer toward this agent id each frame. */
    leaderId?: string;
    /** Set for the chosen leader in a multi-agent group. */
    isGroupLeader?: boolean;
    brushMode: AgentBrushMode;
    /** Filled when brushMode is circles, polygon, or text. */
    stamps: AgentStamp[];
    /** Text brush: string to cycle; each stamp takes the next character (wraps). */
    brushTextSource?: string;
    /** Text brush: next index into `brushTextSource`. */
    brushTextIndex?: number;
    /** Frames since last stamp (brush modes other than stroke; used when spacing mode is frames). */
    stampFramesSinceLast: number;
    /** Distance carry (px) for distance-based stamping; remainder since last stamp. */
    stampDistanceCarry?: number;
    /** Arc length along the nominal path; used when spiral trail is enabled (stroke mode). */
    spiralArcLength: number;
    /** Stroke only: max polyline length in SVG units; sampled at spawn from global settings. */
    trailLengthCap: number;
    /** Stroke only: accumulated trail length. */
    trailArcLength: number;
    /** Remaining frames of gradual velocity kick; optional per-frame impulse below. */
    kickBlendRemaining?: number;
    kickPerFrameVx?: number;
    kickPerFrameVy?: number;
    /** Gradual stroke-width kick: remaining frames and per-frame width delta. */
    strokeWidthBlendRemaining?: number;
    strokeWidthPerFrameW?: number;
    /** Seconds until this agent may spawn another agent at its position; optional when off. */
    childSpawnTimerSec?: number;
    /**
     * Kinematic multi-stroke (stroke mode): this agent only follows `parallelStrokeLeaderId` each frame;
     * no independent physics. Slot index for spacing along the brush normal.
     */
    parallelStrokeLeaderId?: string;
    parallelStrokeSlotIndex?: number;
    /** Set on the primary stroke only: ids of kinematic parallel strokes in this bundle. */
    parallelStrokeSlaveIds?: string[];
}

export interface CompletedAgentPath {
    id: string;
    /** Same as the agent's spawn cohort; omitted on legacy data. */
    spawnGroupId?: string;
    /** Defaults to stroke for older completions. */
    brushMode?: AgentBrushMode;
    points: { x: number; y: number }[];
    /** Optional; when present, enables varying-width export/render. */
    strokeWidths?: number[];
    stamps?: AgentStamp[];
    /** Per-agent color; omitted on legacy completions -> use global stroke color. */
    strokeColor?: string;
}

interface PathAgentsState {
    viewBoxAttr: string;
    width: number;
    height: number;
    bounds: ViewBoxBounds | null;
    /** Snapshot for rendering (updated each animation frame when agents move). */
    activeAgents: PathAgent[];
    completedPaths: CompletedAgentPath[];
    kickIntervalSec: number;
    kickIntervalRandomnessSec: number;
    forceMagnitude: number;
    baseSpeed: number;
    maxSpeed: number;
    /** Constrain motion to axis-aligned (90°) or 8-way (45°) directions; `none` = free. */
    directionLock: DirectionLockMode;
    setDirectionLock: (v: DirectionLockMode) => void;
    /** Stroke trails stop after this arc length (px); randomness varies per agent at spawn. */
    maxTrailLength: number;
    setMaxTrailLength: (v: number) => void;
    /** 0 = each stroke uses `maxTrailLength`; 1 = uniform +/-100% of base (clamped). */
    maxTrailLengthRandomness: number;
    setMaxTrailLengthRandomness: (v: number) => void;
    /** When true (default), agents finish when they hit the artboard edge. */
    stopAtMargin: boolean;
    setStopAtMargin: (v: boolean) => void;
    /** Seconds between child spawns at the agent position; 0 = off. */
    spawnChildIntervalSec: number;
    setSpawnChildIntervalSec: (v: number) => void;
    /** +/- seconds jitter around `spawnChildIntervalSec` for the next delay. */
    spawnChildIntervalRandomnessSec: number;
    setSpawnChildIntervalRandomnessSec: (v: number) => void;
    /** How child agents get their max trail length cap when periodic spawn is on. */
    childTrailCapMode: ChildTrailCapMode;
    setChildTrailCapMode: (v: ChildTrailCapMode) => void;
    /** Used when `childTrailCapMode` is `fixed` (SVG units). */
    childTrailCapFixed: number;
    setChildTrailCapFixed: (v: number) => void;
    /** 0-1: uniform +/-100% jitter applied to the chosen base cap (same convention as max trail randomness). */
    childTrailCapRandomness: number;
    setChildTrailCapRandomness: (v: number) => void;
    /** Vertical acceleration (SVG space: positive pulls toward bottom, negative toward top). */
    gravity: number;
    setGravity: (v: number) => void;
    strokeColor: string;
    /** Extra colors (one per line or comma-separated); used when random stroke color per agent is on. */
    strokePaletteText: string;
    setStrokePaletteText: (v: string) => void;
    randomStrokeColorPerAgent: boolean;
    setRandomStrokeColorPerAgent: (v: boolean) => void;
    strokeWidth: number;
    minStrokeWidth: number;
    setMinStrokeWidth: (v: number) => void;
    initialStrokeWidth: number;
    setInitialStrokeWidth: (v: number) => void;
    backgroundColor: string;
    exportBackground: boolean;
    smoothPaths: boolean;
    setSmoothPaths: (v: boolean) => void;
    /** When stroke width varies along a trail, use one path with average width instead of many line segments. */
    mergeTrailToSinglePath: boolean;
    setMergeTrailToSinglePath: (v: boolean) => void;
    unionGroupOnComplete: boolean;
    setUnionGroupOnComplete: (v: boolean) => void;
    /** Filled polygon outline from per-vertex widths (true variable-width stroke). */
    filledVariableWidthStroke: boolean;
    setFilledVariableWidthStroke: (v: boolean) => void;
    trailTaperStartLength: number;
    setTrailTaperStartLength: (v: number) => void;
    trailTaperEndLength: number;
    setTrailTaperEndLength: (v: number) => void;
    /** Solid stroke when undefined. */
    strokeDashArray: string | undefined;
    dashedStroke: boolean;
    setDashedStroke: (v: boolean) => void;
    strokeDashLength: number;
    setStrokeDashLength: (v: number) => void;
    strokeDashGap: number;
    setStrokeDashGap: (v: number) => void;
    setKickIntervalSec: (v: number) => void;
    setKickIntervalRandomnessSec: (v: number) => void;
    /** When true, each velocity kick is applied as equal steps over `velocityKickBlendFrames`. */
    velocityKickGradual: boolean;
    setVelocityKickGradual: (v: boolean) => void;
    velocityKickBlendFrames: number;
    setVelocityKickBlendFrames: (v: number) => void;
    setForceMagnitude: (v: number) => void;
    setBaseSpeed: (v: number) => void;
    setMaxSpeed: (v: number) => void;
    setStrokeColor: (v: string) => void;
    setStrokeWidth: (v: number) => void;
    strokeWidthStep: number;
    setStrokeWidthStep: (v: number) => void;
    strokeWidthKickFrames: number;
    setStrokeWidthKickFrames: (v: number) => void;
    /** When true, each stroke-width kick is spread over `strokeWidthBlendFrames`. */
    strokeWidthKickGradual: boolean;
    setStrokeWidthKickGradual: (v: boolean) => void;
    strokeWidthBlendFrames: number;
    setStrokeWidthBlendFrames: (v: number) => void;
    setBackgroundColor: (v: string) => void;
    setExportBackground: (v: boolean) => void;
    inputMode: AgentInputMode;
    setInputMode: (v: AgentInputMode) => void;
    setPuppetBrushTarget: (spawnGroupId: string, x: number, y: number) => void;
    clearPuppetBrush: () => void;
    addAgentAt: (
        x: number,
        y: number,
        initialDir?: { dx: number; dy: number } | null,
        options?: AddAgentAtOptions,
    ) => string | undefined;
    clearPaths: () => void;
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    randomizeParameters: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    groupSize: number;
    setGroupSize: (v: number) => void;
    groupRadius: number;
    setGroupRadius: (v: number) => void;
    groupCohesionStrength: number;
    setGroupCohesionStrength: (v: number) => void;
    /** When true and group size is greater than 1, each member gets its own random brush mode. */
    groupVariedBrushes: boolean;
    setGroupVariedBrushes: (v: boolean) => void;
    /** Stroke mode: parallel stroke origins per group slot (multiplies with group size). */
    strokeLayoutCount: number;
    setStrokeLayoutCount: (v: number) => void;
    strokeLayoutSpacing: number;
    setStrokeLayoutSpacing: (v: number) => void;
    agentBrushMode: AgentBrushMode;
    setAgentBrushMode: (v: AgentBrushMode) => void;
    brushStampIntervalFrames: number;
    setBrushStampIntervalFrames: (v: number) => void;
    brushPolygonSides: number;
    setBrushPolygonSides: (v: number) => void;
    brushStampSizeScale: number;
    setBrushStampSizeScale: (v: number) => void;
    /** Stamp brushes: time (frames) vs. arc length (px) between samples. */
    brushStampSpacingMode: BrushStampSpacingMode;
    setBrushStampSpacingMode: (v: BrushStampSpacingMode) => void;
    /** Used when `brushStampSpacingMode` is `distance` (SVG px along motion). */
    brushStampDistancePx: number;
    setBrushStampDistancePx: (v: number) => void;
    /** Text brush: custom string; empty uses random text per agent at spawn. */
    brushTextContent: string;
    setBrushTextContent: (v: string) => void;
    /** Text+stroke brush: multiplies trail width behind glyphs. */
    brushTextStrokeUnderlayScale: number;
    setBrushTextStrokeUnderlayScale: (v: number) => void;
    /** Text+stroke brush: underlay stroke color. */
    brushTextStrokeUnderlayColor: string;
    setBrushTextStrokeUnderlayColor: (v: string) => void;
    /** Text brush: each new agent starts at a random offset in its text source. */
    brushTextRandomStartOffset: boolean;
    setBrushTextRandomStartOffset: (v: boolean) => void;
    /** When true, SVG export wraps each text-brush agent's glyphs in one `<g data-agent-id>`. */
    exportGroupTextByAgent: boolean;
    setExportGroupTextByAgent: (v: boolean) => void;
    /** Wiggle the stroke perpendicular to motion (spiral-like; stroke mode only). */
    spiralTrail: boolean;
    setSpiralTrail: (v: boolean) => void;
    spiralAmplitude: number;
    setSpiralAmplitude: (v: number) => void;
    /** Distance along the path for one full lateral cycle. */
    spiralWavelength: number;
    setSpiralWavelength: (v: number) => void;
    /** Scales amplitude with distance (0 = constant wave). */
    spiralGrowthPer1k: number;
    setSpiralGrowthPer1k: (v: number) => void;
    /** Imported SVG whose geometry defines spawn positions (not rendered). */
    spawnGuideSvg: { name: string; content: string } | null;
    setSpawnGuideSvg: (v: { name: string; content: string } | null) => void;
    /** Approximate spacing along guide geometry when sampling (SVG px). */
    spawnGuideSamplePx: number;
    setSpawnGuideSamplePx: (v: number) => void;
    /** Hard cap on how many agents one "Spawn from guide" run creates. */
    spawnGuideMaxAgents: number;
    setSpawnGuideMaxAgents: (v: number) => void;
    /** When true, each agent's initial motion follows the sampled tangent. */
    spawnGuideAlignTangent: boolean;
    setSpawnGuideAlignTangent: (v: boolean) => void;
    spawnGuideError: string | null;
    setSpawnGuideError: (v: string | null) => void;
    clearSpawnGuide: () => void;
    /** Spawns agents at sampled guide positions; returns count placed, or 0 on failure. */
    spawnAgentsFromImportedGuide: () => number;
}

const PathAgentsContext = createContext<PathAgentsState | null>(null);

export function usePathAgents() {
    const ctx = useContext(PathAgentsContext);
    if (!ctx) {
        throw new Error("usePathAgents must be used within PathAgentsProvider");
    }
    return ctx;
}

const VIEW_BOX_ATTR = "0 0 1000 1000";

function escapeSvgDataAttr(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function rf(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function ri(min: number, max: number): number {
    return Math.floor(rf(min, max + 1));
}

function initialVelocityFromDir(
    initialDir: { dx: number; dy: number } | null | undefined,
    baseSpd: number,
    vMax: number,
): { vx: number; vy: number } {
    if (initialDir) {
        const dlen = Math.hypot(initialDir.dx, initialDir.dy);
        if (dlen >= 1e-9) {
            const ux = initialDir.dx / dlen;
            const uy = initialDir.dy / dlen;
            return clampSpeed(ux * baseSpd, uy * baseSpd, vMax);
        }
    }
    const { vx, vy } = randomVelocity(baseSpd);
    return clampSpeed(vx, vy, vMax);
}

function hexByte(lo: number, hi: number): string {
    return Math.floor(rf(lo, hi + 1))
        .toString(16)
        .padStart(2, "0");
}

/** When a leader hits the edge, promote one follower to lead the rest. */
function repairGroupsAfterLeaderLoss(agents: PathAgent[]): PathAgent[] {
    const idSet = new Set(agents.map((a) => a.id));
    const byDeadLeader = new Map<string, PathAgent[]>();
    for (const a of agents) {
        if (a.leaderId && !idSet.has(a.leaderId)) {
            const list = byDeadLeader.get(a.leaderId) ?? [];
            list.push(a);
            byDeadLeader.set(a.leaderId, list);
        }
    }
    if (byDeadLeader.size === 0) return agents;

    const patch = new Map<string, Partial<PathAgent>>();
    for (const [, members] of byDeadLeader) {
        if (members.length === 0) continue;
        const nl = members[0];
        patch.set(nl.id, { leaderId: undefined, isGroupLeader: true });
        for (let i = 1; i < members.length; i++) {
            patch.set(members[i].id, {
                leaderId: nl.id,
                isGroupLeader: false,
            });
        }
    }
    return agents.map((a) => ({ ...a, ...(patch.get(a.id) ?? {}) }));
}

interface PathAgentsProviderProps {
    children: React.ReactNode;
}

export function PathAgentsProvider({ children }: PathAgentsProviderProps) {
    const vbParts = useMemo(() => VIEW_BOX_ATTR.split(/\s+/).map(Number), []);
    const width = vbParts[2] ?? 1000;
    const height = vbParts[3] ?? 1000;

    const bounds = useMemo(() => parseViewBoxBounds(VIEW_BOX_ATTR), []);

    const agentsRef = useRef<PathAgent[]>([]);
    const puppetBrushRef = useRef<{
        spawnGroupId: string;
        x: number;
        y: number;
    } | null>(null);
    const [tick, setTick] = useState(0);
    const bump = useCallback(() => setTick((t) => t + 1), []);

    const [inputMode, setInputMode] = useState<AgentInputMode>(
        DEFAULT_AGENT_INPUT_MODE,
    );

    const setPuppetBrushTarget = useCallback(
        (spawnGroupId: string, x: number, y: number) => {
            puppetBrushRef.current = { spawnGroupId, x, y };
        },
        [],
    );

    const clearPuppetBrush = useCallback(() => {
        puppetBrushRef.current = null;
    }, []);

    const [completedPaths, setCompletedPaths] = useState<CompletedAgentPath[]>(
        [],
    );

    const [kickIntervalSec, setKickIntervalSec] = useState(1);
    const [kickIntervalRandomnessSec, setKickIntervalRandomnessSec] =
        useState(0);
    const [velocityKickGradual, setVelocityKickGradual] = useState(false);
    const [velocityKickBlendFrames, setVelocityKickBlendFrames] = useState(10);
    const [forceMagnitude, setForceMagnitude] = useState(80);
    const [baseSpeed, setBaseSpeed] = useState(120);
    const [maxSpeed, setMaxSpeed] = useState(400);
    const [directionLock, setDirectionLock] =
        useState<DirectionLockMode>("none");
    const [maxTrailLength, setMaxTrailLength] = useState(2400);
    const [maxTrailLengthRandomness, setMaxTrailLengthRandomness] = useState(0);
    const [stopAtMargin, setStopAtMargin] = useState(true);
    const [spawnChildIntervalSec, setSpawnChildIntervalSec] = useState(0);
    const [
        spawnChildIntervalRandomnessSec,
        setSpawnChildIntervalRandomnessSec,
    ] = useState(0);
    const [childTrailCapMode, setChildTrailCapMode] =
        useState<ChildTrailCapMode>("none");
    const [childTrailCapFixed, setChildTrailCapFixed] = useState(2400);
    const [childTrailCapRandomness, setChildTrailCapRandomness] = useState(0);
    const [gravity, setGravity] = useState(0);
    const [strokeColor, setStrokeColor] = useState("#e0e0e0");
    const [strokePaletteText, setStrokePaletteText] = useState("");
    const [randomStrokeColorPerAgent, setRandomStrokeColorPerAgent] =
        useState(false);
    /** Maximum live stroke width. */
    const [strokeWidth, setStrokeWidth] = useState(8);
    const [minStrokeWidth, setMinStrokeWidth] = useState(0.25);
    const [initialStrokeWidth, setInitialStrokeWidth] = useState(1);
    const [strokeWidthStep, setStrokeWidthStep] = useState(0.5);
    const [strokeWidthKickFrames, setStrokeWidthKickFrames] = useState(15);
    const [strokeWidthKickGradual, setStrokeWidthKickGradual] = useState(false);
    const [strokeWidthBlendFrames, setStrokeWidthBlendFrames] = useState(10);
    const [backgroundColor, setBackgroundColor] = useState("#1a1a1a");
    const [exportBackground, setExportBackground] = useState(false);
    const [exportGroupTextByAgent, setExportGroupTextByAgent] = useState(false);
    const [smoothPaths, setSmoothPaths] = useState(false);
    const [mergeTrailToSinglePath, setMergeTrailToSinglePath] = useState(true);
    const [unionGroupOnComplete, setUnionGroupOnComplete] = useState(true);
    const [filledVariableWidthStroke, setFilledVariableWidthStroke] =
        useState(false);
    const [trailTaperStartLength, setTrailTaperStartLength] = useState(0);
    const [trailTaperEndLength, setTrailTaperEndLength] = useState(0);
    const [dashedStroke, setDashedStroke] = useState(false);
    const [strokeDashLength, setStrokeDashLength] = useState(16);
    const [strokeDashGap, setStrokeDashGap] = useState(12);

    const [spawnGuideSvg, setSpawnGuideSvg] = useState<{
        name: string;
        content: string;
    } | null>(null);
    const [spawnGuideSamplePx, setSpawnGuideSamplePx] = useState(14);
    const [spawnGuideMaxAgents, setSpawnGuideMaxAgents] = useState(400);
    const [spawnGuideAlignTangent, setSpawnGuideAlignTangent] = useState(true);
    const [spawnGuideError, setSpawnGuideError] = useState<string | null>(null);

    const strokeDashArray = useMemo(
        () =>
            strokeDashArrayFromSettings(
                dashedStroke,
                strokeDashLength,
                strokeDashGap,
            ),
        [dashedStroke, strokeDashLength, strokeDashGap],
    );

    const [groupSize, setGroupSize] = useState(1);
    const [groupRadius, setGroupRadius] = useState(80);
    const [strokeLayoutCount, setStrokeLayoutCount] = useState(1);
    const [strokeLayoutSpacing, setStrokeLayoutSpacing] = useState(12);
    const [groupCohesionStrength, setGroupCohesionStrength] = useState(6);
    const [groupVariedBrushes, setGroupVariedBrushes] = useState(true);

    const [agentBrushMode, setAgentBrushMode] =
        useState<AgentBrushMode>("stroke");
    const [brushStampIntervalFrames, setBrushStampIntervalFrames] = useState(8);
    const [brushPolygonSides, setBrushPolygonSides] = useState(6);
    const [brushStampSizeScale, setBrushStampSizeScale] = useState(1.5);
    const [brushStampSpacingMode, setBrushStampSpacingMode] =
        useState<BrushStampSpacingMode>("frames");
    const [brushStampDistancePx, setBrushStampDistancePx] = useState(24);
    const [brushTextContent, setBrushTextContent] = useState("");
    const [brushTextStrokeUnderlayColor, setBrushTextStrokeUnderlayColor] =
        useState("#111111");
    const [brushTextStrokeUnderlayScale, setBrushTextStrokeUnderlayScale] =
        useState(1.75);
    const [brushTextRandomStartOffset, setBrushTextRandomStartOffset] =
        useState(false);

    const isTrailBrushMode = useCallback(
        (mode: AgentBrushMode) =>
            mode === "stroke" || mode === "textWithStroke",
        [],
    );

    const [spiralTrail, setSpiralTrail] = useState(false);
    const [spiralAmplitude, setSpiralAmplitude] = useState(14);
    const [spiralWavelength, setSpiralWavelength] = useState(80);
    const [spiralGrowthPer1k, setSpiralGrowthPer1k] = useState(0.15);

    useEffect(() => {
        setStrokeWidth((w) => Math.max(w, minStrokeWidth));
    }, [minStrokeWidth]);

    useEffect(() => {
        setInitialStrokeWidth((w) =>
            Math.min(strokeWidth, Math.max(minStrokeWidth, w)),
        );
    }, [strokeWidth, minStrokeWidth]);

    const idCounterRef = useRef(0);
    const spawnGroupCounterRef = useRef(0);

    const paramsRef = useRef({
        kickIntervalSec,
        kickIntervalRandomnessSec,
        velocityKickGradual,
        velocityKickBlendFrames,
        forceMagnitude,
        maxSpeed,
        directionLock,
        gravity,
        strokeColor,
        strokePaletteText,
        randomStrokeColorPerAgent,
        strokeWidth,
        minStrokeWidth,
        strokeWidthStep,
        strokeWidthKickFrames,
        strokeWidthKickGradual,
        strokeWidthBlendFrames,
        groupCohesionStrength,
        brushStampIntervalFrames,
        brushPolygonSides,
        brushStampSizeScale,
        spiralTrail,
        spiralAmplitude,
        spiralWavelength,
        spiralGrowthPer1k,
        maxTrailLength,
        maxTrailLengthRandomness,
        spawnChildIntervalSec,
        spawnChildIntervalRandomnessSec,
        childTrailCapMode,
        childTrailCapFixed,
        childTrailCapRandomness,
        stopAtMargin,
        strokeLayoutCount,
        strokeLayoutSpacing,
        brushTextContent,
        brushTextStrokeUnderlayColor,
        brushTextStrokeUnderlayScale,
        brushTextRandomStartOffset,
        brushStampSpacingMode,
        brushStampDistancePx,
    });
    paramsRef.current = {
        kickIntervalSec,
        kickIntervalRandomnessSec,
        velocityKickGradual,
        velocityKickBlendFrames,
        forceMagnitude,
        maxSpeed,
        directionLock,
        gravity,
        strokeColor,
        strokePaletteText,
        randomStrokeColorPerAgent,
        strokeWidth,
        minStrokeWidth,
        strokeWidthStep,
        strokeWidthKickFrames,
        strokeWidthKickGradual,
        strokeWidthBlendFrames,
        groupCohesionStrength,
        brushStampIntervalFrames,
        brushPolygonSides,
        brushStampSizeScale,
        spiralTrail,
        spiralAmplitude,
        spiralWavelength,
        spiralGrowthPer1k,
        maxTrailLength,
        maxTrailLengthRandomness,
        spawnChildIntervalSec,
        spawnChildIntervalRandomnessSec,
        childTrailCapMode,
        childTrailCapFixed,
        childTrailCapRandomness,
        stopAtMargin,
        strokeLayoutCount,
        strokeLayoutSpacing,
        brushTextContent,
        brushTextStrokeUnderlayColor,
        brushTextStrokeUnderlayScale,
        brushTextRandomStartOffset,
        brushStampSpacingMode,
        brushStampDistancePx,
    };

    /** Seconds until next kick; `null` when there are no agents (reset on next spawn). */
    const timeToNextKickRef = useRef<number | null>(null);

    const frameIdxRef = useRef(0);

    useEffect(() => {
        if (!bounds) return;

        let raf = 0;
        let last = performance.now();

        const loop = (now: number) => {
            const dt = Math.min(0.1, (now - last) / 1000);
            last = now;

            frameIdxRef.current += 1;

            const {
                kickIntervalSec: kInt,
                kickIntervalRandomnessSec: kJitter,
                velocityKickGradual: gradualKick,
                velocityKickBlendFrames: kickBlendN,
                forceMagnitude: fMag,
                maxSpeed: vMax,
                directionLock: dirLock,
                gravity: grav,
                strokeWidth: maxStrokeW,
                minStrokeWidth: minStrokeW,
                strokeWidthStep: swStep,
                strokeWidthKickFrames: swKickFrames,
                strokeWidthKickGradual: gradualSwKick,
                strokeWidthBlendFrames: swBlendN,
                groupCohesionStrength: cohesionK,
                brushStampIntervalFrames: stampEvery,
                brushPolygonSides: polySides,
                brushStampSizeScale: stampScale,
                brushStampSpacingMode: stampSpacingMode,
                brushStampDistancePx: stampDistPx,
                spiralTrail: spiralOn,
                spiralAmplitude: spiralAmp,
                spiralWavelength: spiralWave,
                spiralGrowthPer1k: spiralGrowth,
                maxTrailLength: maxTrailLen,
                maxTrailLengthRandomness: maxTrailRand,
                spawnChildIntervalSec: sci,
                spawnChildIntervalRandomnessSec: scr,
                childTrailCapMode: childTrailCapModeParam,
                childTrailCapFixed: childTrailCapFixedParam,
                childTrailCapRandomness: childTrailCapRandomnessParam,
                stopAtMargin: stopAtEdge,
                strokeLayoutCount: strokeLayoutCountParam,
                strokeLayoutSpacing: strokeLayoutSpacingParam,
            } = paramsRef.current;

            const strokeLayoutK = Math.max(
                1,
                Math.min(32, Math.round(strokeLayoutCountParam)),
            );
            const strokeLayoutSpacingPx = Math.max(0, strokeLayoutSpacingParam);

            if (agentsRef.current.length === 0) {
                timeToNextKickRef.current = null;
            } else {
                if (timeToNextKickRef.current === null) {
                    timeToNextKickRef.current = sampleKickDelay(kInt, kJitter);
                }
                timeToNextKickRef.current -= dt;
                while (
                    timeToNextKickRef.current <= 0 &&
                    agentsRef.current.length > 0
                ) {
                    agentsRef.current = agentsRef.current.map((a) => {
                        if (a.parallelStrokeLeaderId) return a;
                        const k = applyVelocityKick(a.vx, a.vy, fMag, vMax);
                        if (gradualKick) {
                            const N = Math.max(1, Math.round(kickBlendN));
                            return {
                                ...a,
                                kickPerFrameVx: (k.vx - a.vx) / N,
                                kickPerFrameVy: (k.vy - a.vy) / N,
                                kickBlendRemaining: N,
                            };
                        }
                        return {
                            ...a,
                            vx: k.vx,
                            vy: k.vy,
                            kickPerFrameVx: undefined,
                            kickPerFrameVy: undefined,
                            kickBlendRemaining: undefined,
                        };
                    });
                    timeToNextKickRef.current += sampleKickDelay(kInt, kJitter);
                }
            }

            if (
                agentsRef.current.length > 0 &&
                swKickFrames > 0 &&
                frameIdxRef.current % swKickFrames === 0
            ) {
                agentsRef.current = agentsRef.current.map((a) => {
                    if (a.parallelStrokeLeaderId) return a;
                    const target = applyStrokeWidthKick(
                        a.liveStrokeWidth,
                        swStep,
                        minStrokeW,
                        maxStrokeW,
                    );
                    if (gradualSwKick) {
                        const N = Math.max(1, Math.round(swBlendN));
                        return {
                            ...a,
                            strokeWidthPerFrameW:
                                (target - a.liveStrokeWidth) / N,
                            strokeWidthBlendRemaining: N,
                        };
                    }
                    return {
                        ...a,
                        liveStrokeWidth: target,
                        strokeWidthPerFrameW: undefined,
                        strokeWidthBlendRemaining: undefined,
                    };
                });
            }

            const byId = new Map(
                agentsRef.current.map((a) => [a.id, a] as const),
            );

            const nextAgents: PathAgent[] = [];
            const finished: CompletedAgentPath[] = [];
            const childSpawns: PathAgent[] = [];

            const makePathChildAgents = (
                parent: PathAgent,
                sx: number,
                sy: number,
                cvx: number,
                cvy: number,
                wChild: number,
                parentStrokeRemaining?: number,
            ): PathAgent[] => {
                const assignStroke = () =>
                    resolveAgentStrokeColor({
                        randomPerAgent:
                            paramsRef.current.randomStrokeColorPerAgent,
                        paletteText: paramsRef.current.strokePaletteText,
                        primaryStrokeColor: paramsRef.current.strokeColor,
                    });
                const mode = parent.brushMode;
                const childLayoutK =
                    mode === "stroke"
                        ? Math.max(
                              1,
                              Math.min(32, Math.round(strokeLayoutCountParam)),
                          )
                        : 1;
                const childLayoutSpacing = Math.max(
                    0,
                    strokeLayoutSpacingParam,
                );

                const trail = isTrailBrushMode(mode)
                    ? {
                          trailLengthCap: computeChildTrailLengthCap({
                              mode: childTrailCapModeParam,
                              maxTrailLength: maxTrailLen,
                              maxTrailLengthRandomness: maxTrailRand,
                              parentRemaining: parentStrokeRemaining,
                              fixedCap: childTrailCapFixedParam,
                              childTrailCapRandomness:
                                  childTrailCapRandomnessParam,
                          }),
                          trailArcLength: 0,
                      }
                    : {
                          trailLengthCap: MAX_TRAIL_LENGTH_CAP,
                          trailArcLength: 0,
                      };
                const brushFor = (
                    bx: number,
                    by: number,
                ): Pick<
                    PathAgent,
                    | "brushMode"
                    | "stamps"
                    | "stampFramesSinceLast"
                    | "brushTextSource"
                    | "brushTextIndex"
                    | "stampDistanceCarry"
                > => {
                    if (mode === "stroke") {
                        return {
                            brushMode: "stroke",
                            stamps: [],
                            stampFramesSinceLast: 0,
                        };
                    }
                    if (mode === "text" || mode === "textWithStroke") {
                        const src =
                            parent.brushTextSource ??
                            resolveBrushTextInput(
                                paramsRef.current.brushTextContent,
                            );
                        const initialIndex =
                            paramsRef.current.brushTextRandomStartOffset &&
                            src.length > 0
                                ? ri(0, src.length - 1)
                                : 0;
                        const { char, nextIndex } = pickNextBrushTextChar(
                            src,
                            initialIndex,
                        );
                        return {
                            brushMode: mode,
                            stamps: [
                                createTextStamp(
                                    bx,
                                    by,
                                    wChild,
                                    stampScale,
                                    char,
                                    cvx,
                                    cvy,
                                ),
                            ],
                            stampFramesSinceLast: 0,
                            brushTextSource: src,
                            brushTextIndex: nextIndex,
                            stampDistanceCarry: 0,
                        };
                    }
                    return {
                        brushMode: mode,
                        stamps: [
                            createBrushStamp(
                                mode === "circles" ? "circles" : "polygon",
                                bx,
                                by,
                                wChild,
                                polySides,
                                stampScale,
                            ),
                        ],
                        stampFramesSinceLast: 0,
                        stampDistanceCarry: 0,
                    };
                };

                if (mode !== "stroke" || childLayoutK === 1) {
                    if (stopAtEdge && isOutsideOrOnEdge(sx, sy, bounds)) {
                        return [];
                    }
                    idCounterRef.current += 1;
                    const id = `agent-${idCounterRef.current}`;
                    return [
                        {
                            id,
                            spawnGroupId: parent.spawnGroupId,
                            x: sx,
                            y: sy,
                            vx: cvx,
                            vy: cvy,
                            points: [{ x: sx, y: sy }],
                            strokeWidths: [wChild],
                            liveStrokeWidth: wChild,
                            strokeColor: assignStroke(),
                            spiralArcLength: 0,
                            ...trail,
                            ...brushFor(sx, sy),
                            childSpawnTimerSec:
                                sci > 0 ? sampleKickDelay(sci, scr) : undefined,
                        },
                    ];
                }

                const { nx: perpNx, ny: perpNy } = unitPerpendicularToMotion(
                    null,
                    cvx,
                    cvy,
                );
                const layoutMid = Math.floor((childLayoutK - 1) / 2);
                const slotIds: string[] = [];
                for (let j = 0; j < childLayoutK; j++) {
                    idCounterRef.current += 1;
                    slotIds.push(`agent-${idCounterRef.current}`);
                }
                const primaryId = slotIds[layoutMid]!;
                const slaveIds = slotIds.filter((_, idx) => idx !== layoutMid);

                const out: PathAgent[] = [];
                for (let j = 0; j < childLayoutK; j++) {
                    const t = j - (childLayoutK - 1) / 2;
                    const ax = sx + perpNx * childLayoutSpacing * t;
                    const ay = sy + perpNy * childLayoutSpacing * t;
                    const c = clampPointInsideBounds(ax, ay, bounds);
                    if (stopAtEdge && isOutsideOrOnEdge(c.x, c.y, bounds)) {
                        if (j === layoutMid) {
                            return [];
                        }
                        continue;
                    }
                    const id = slotIds[j]!;
                    const isPrimary = j === layoutMid;
                    let parallelStrokeLeaderId: string | undefined;
                    let parallelStrokeSlotIndex: number | undefined;
                    let parallelStrokeSlaveIds: string[] | undefined;
                    if (isPrimary) {
                        parallelStrokeSlaveIds = slaveIds;
                    } else {
                        parallelStrokeLeaderId = primaryId;
                        parallelStrokeSlotIndex = j;
                    }
                    out.push({
                        id,
                        spawnGroupId: parent.spawnGroupId,
                        x: c.x,
                        y: c.y,
                        vx: cvx,
                        vy: cvy,
                        points: [{ x: c.x, y: c.y }],
                        strokeWidths: [wChild],
                        liveStrokeWidth: wChild,
                        strokeColor: assignStroke(),
                        spiralArcLength: 0,
                        parallelStrokeLeaderId,
                        parallelStrokeSlotIndex,
                        parallelStrokeSlaveIds,
                        ...trail,
                        ...brushFor(c.x, c.y),
                        childSpawnTimerSec:
                            sci > 0 ? sampleKickDelay(sci, scr) : undefined,
                    });
                }
                return out;
            };

            const finishParallelSlavesForPrimary = (primary: PathAgent) => {
                if (!primary.parallelStrokeSlaveIds?.length) return;
                for (const sid of primary.parallelStrokeSlaveIds) {
                    const s = agentsRef.current.find((a) => a.id === sid);
                    if (s && s.points.length >= 2) {
                        finished.push({
                            id: s.id,
                            spawnGroupId: s.spawnGroupId,
                            brushMode: "stroke",
                            points: s.points,
                            strokeWidths:
                                s.strokeWidths.length === s.points.length
                                    ? s.strokeWidths
                                    : undefined,
                            strokeColor: s.strokeColor,
                        });
                    }
                }
            };

            for (const agent of agentsRef.current) {
                if (agent.parallelStrokeLeaderId) {
                    continue;
                }
                let vx = agent.vx;
                let vy = agent.vy;
                const kRem = agent.kickBlendRemaining ?? 0;
                if (
                    kRem > 0 &&
                    agent.kickPerFrameVx !== undefined &&
                    agent.kickPerFrameVy !== undefined
                ) {
                    vx += agent.kickPerFrameVx;
                    vy += agent.kickPerFrameVy;
                }

                let liveW = agent.liveStrokeWidth;
                const swRem = agent.strokeWidthBlendRemaining ?? 0;
                if (swRem > 0 && agent.strokeWidthPerFrameW !== undefined) {
                    liveW = Math.min(
                        maxStrokeW,
                        Math.max(
                            minStrokeW,
                            agent.liveStrokeWidth + agent.strokeWidthPerFrameW,
                        ),
                    );
                }

                if (agent.leaderId) {
                    const L = byId.get(agent.leaderId);
                    if (L) {
                        vx += cohesionK * (L.x - agent.x) * dt;
                        vy += cohesionK * (L.y - agent.y) * dt;
                        const sp = clampSpeed(vx, vy, vMax);
                        vx = sp.vx;
                        vy = sp.vy;
                    }
                }

                const pup = puppetBrushRef.current;
                if (
                    pup &&
                    agent.leaderId == null &&
                    agent.spawnGroupId === pup.spawnGroupId
                ) {
                    vx += PUPPET_PULL_K * (pup.x - agent.x) * dt;
                    vy += PUPPET_PULL_K * (pup.y - agent.y) * dt;
                    const sp = clampSpeed(vx, vy, vMax);
                    vx = sp.vx;
                    vy = sp.vy;
                }

                vy += grav * dt;
                {
                    const sp = clampSpeed(vx, vy, vMax);
                    vx = sp.vx;
                    vy = sp.vy;
                }
                if (dirLock !== "none") {
                    const snapped = snapVelocityToDirectionLock(
                        vx,
                        vy,
                        dirLock,
                    );
                    vx = snapped.vx;
                    vy = snapped.vy;
                }

                const nx = agent.x + vx * dt;
                const ny = agent.y + vy * dt;

                let px = nx;
                let py = ny;
                let nextSpiralArc = agent.spiralArcLength ?? 0;
                if (spiralOn && isTrailBrushMode(agent.brushMode)) {
                    const sp = applySpiralTrailOffset(
                        nx,
                        ny,
                        vx,
                        vy,
                        dt,
                        agent.spiralArcLength ?? 0,
                        spiralAmp,
                        spiralWave,
                        spiralGrowth,
                    );
                    px = sp.x;
                    py = sp.y;
                    nextSpiralArc = sp.arcLength;
                }

                if (isTrailBrushMode(agent.brushMode)) {
                    const cap = agent.trailLengthCap;
                    const remaining = cap - agent.trailArcLength;
                    if (remaining <= 0) {
                        if (agent.points.length >= 2) {
                            finished.push({
                                id: agent.id,
                                spawnGroupId: agent.spawnGroupId,
                                brushMode: agent.brushMode,
                                points: agent.points,
                                strokeWidths:
                                    agent.strokeWidths.length ===
                                    agent.points.length
                                        ? agent.strokeWidths
                                        : undefined,
                                strokeColor: agent.strokeColor,
                            });
                            finishParallelSlavesForPrimary(agent);
                        }
                        continue;
                    }
                    const ax = agent.x;
                    const ay = agent.y;
                    const stepDistForTrail = Math.hypot(px - ax, py - ay);
                    if (stepDistForTrail <= 1e-12) {
                        if (agent.points.length >= 2) {
                            finished.push({
                                id: agent.id,
                                spawnGroupId: agent.spawnGroupId,
                                brushMode: agent.brushMode,
                                points: agent.points,
                                strokeWidths:
                                    agent.strokeWidths.length ===
                                    agent.points.length
                                        ? agent.strokeWidths
                                        : undefined,
                                strokeColor: agent.strokeColor,
                            });
                            finishParallelSlavesForPrimary(agent);
                        }
                        continue;
                    }
                    if (stepDistForTrail > remaining) {
                        const s = remaining / stepDistForTrail;
                        px = ax + (px - ax) * s;
                        py = ay + (py - ay) * s;
                    }
                }

                if (stopAtEdge && isOutsideOrOnEdge(px, py, bounds)) {
                    const bMode = agent.brushMode;
                    const hasStrokeTrail =
                        isTrailBrushMode(bMode) && agent.points.length > 0;
                    const hasStamps =
                        (bMode === "text" || bMode === "textWithStroke") &&
                        (agent.stamps?.length ?? 0) > 0;
                    if (hasStrokeTrail || hasStamps) {
                        finished.push({
                            id: agent.id,
                            spawnGroupId: agent.spawnGroupId,
                            brushMode: bMode,
                            points: isTrailBrushMode(bMode) ? agent.points : [],
                            strokeWidths:
                                isTrailBrushMode(bMode) &&
                                agent.strokeWidths.length ===
                                    agent.points.length
                                    ? agent.strokeWidths
                                    : undefined,
                            stamps:
                                bMode === "text" || bMode === "textWithStroke"
                                    ? agent.stamps
                                    : undefined,
                            strokeColor: agent.strokeColor,
                        });
                        if (bMode === "stroke") {
                            finishParallelSlavesForPrimary(agent);
                        }
                    }
                    continue;
                }

                const nextKickRem = kRem > 0 ? kRem - 1 : 0;
                const nextKickPx =
                    nextKickRem > 0 ? agent.kickPerFrameVx : undefined;
                const nextKickPy =
                    nextKickRem > 0 ? agent.kickPerFrameVy : undefined;

                const nextSwRem = swRem > 0 ? swRem - 1 : 0;
                const nextSwPw =
                    nextSwRem > 0 ? agent.strokeWidthPerFrameW : undefined;

                if (isTrailBrushMode(agent.brushMode)) {
                    const newTrailArc =
                        agent.trailArcLength +
                        Math.hypot(px - agent.x, py - agent.y);
                    if (newTrailArc >= agent.trailLengthCap - 1e-6) {
                        finished.push({
                            id: agent.id,
                            spawnGroupId: agent.spawnGroupId,
                            brushMode: agent.brushMode,
                            points: [...agent.points, { x: px, y: py }],
                            strokeWidths:
                                agent.strokeWidths.length ===
                                agent.points.length
                                    ? [...agent.strokeWidths, liveW]
                                    : undefined,
                            strokeColor: agent.strokeColor,
                        });
                        if (agent.brushMode === "stroke") {
                            finishParallelSlavesForPrimary(agent);
                        }
                        continue;
                    }
                    let stamps = agent.stamps;
                    let nextBrushTextIndex = agent.brushTextIndex;
                    let stampFramesSinceLast = agent.stampFramesSinceLast;
                    let stampDistanceCarry = agent.stampDistanceCarry;
                    if (agent.brushMode === "textWithStroke") {
                        stamps = agent.stamps ?? [];
                        nextBrushTextIndex = agent.brushTextIndex ?? 0;
                        const interval = Math.max(1, Math.round(stampEvery));
                        const distPx = Math.max(1, stampDistPx);
                        stampFramesSinceLast = agent.stampFramesSinceLast ?? 0;
                        stampDistanceCarry = agent.stampDistanceCarry ?? 0;
                        if (stampSpacingMode === "frames") {
                            stampFramesSinceLast += 1;
                            if (stampFramesSinceLast >= interval) {
                                stampFramesSinceLast = 0;
                                const src = agent.brushTextSource ?? "?";
                                const picked = pickNextBrushTextChar(
                                    src,
                                    nextBrushTextIndex,
                                );
                                nextBrushTextIndex = picked.nextIndex;
                                stamps = [
                                    ...stamps,
                                    createTextStamp(
                                        px,
                                        py,
                                        liveW,
                                        stampScale,
                                        picked.char,
                                        vx,
                                        vy,
                                    ),
                                ];
                            }
                        } else {
                            stampFramesSinceLast = 0;
                            const { positions, carryNext } =
                                stampPositionsAlongSegment(
                                    distPx,
                                    stampDistanceCarry,
                                    agent.x,
                                    agent.y,
                                    px,
                                    py,
                                );
                            stampDistanceCarry = carryNext;
                            const src = agent.brushTextSource ?? "?";
                            for (const p of positions) {
                                const picked = pickNextBrushTextChar(
                                    src,
                                    nextBrushTextIndex,
                                );
                                nextBrushTextIndex = picked.nextIndex;
                                stamps = [
                                    ...stamps,
                                    createTextStamp(
                                        p.x,
                                        p.y,
                                        liveW,
                                        stampScale,
                                        picked.char,
                                        vx,
                                        vy,
                                    ),
                                ];
                            }
                        }
                    }
                    const { nextSeconds: nextChildT, shouldSpawn } =
                        advancePeriodicSpawnTimer(
                            agent.childSpawnTimerSec,
                            dt,
                            sci,
                            scr,
                        );
                    nextAgents.push({
                        ...agent,
                        x: px,
                        y: py,
                        vx,
                        vy,
                        liveStrokeWidth: liveW,
                        points: [...agent.points, { x: px, y: py }],
                        strokeWidths: [...agent.strokeWidths, liveW],
                        stamps,
                        stampFramesSinceLast,
                        stampDistanceCarry:
                            stampSpacingMode === "distance"
                                ? stampDistanceCarry
                                : undefined,
                        brushTextIndex:
                            agent.brushMode === "textWithStroke"
                                ? nextBrushTextIndex
                                : agent.brushTextIndex,
                        spiralArcLength: nextSpiralArc,
                        trailArcLength: newTrailArc,
                        childSpawnTimerSec: nextChildT,
                        kickBlendRemaining: nextKickRem,
                        kickPerFrameVx: nextKickPx,
                        kickPerFrameVy: nextKickPy,
                        strokeWidthBlendRemaining: nextSwRem,
                        strokeWidthPerFrameW: nextSwPw,
                    });
                    if (shouldSpawn) {
                        childSpawns.push(
                            ...makePathChildAgents(
                                agent,
                                px,
                                py,
                                vx,
                                vy,
                                liveW,
                                agent.trailLengthCap - newTrailArc,
                            ),
                        );
                    }
                } else {
                    let stamps = agent.stamps ?? [];
                    let nextBrushTextIndex = agent.brushTextIndex ?? 0;
                    const interval = Math.max(1, Math.round(stampEvery));
                    const distPx = Math.max(1, stampDistPx);

                    let stampFramesSinceLast = agent.stampFramesSinceLast ?? 0;
                    let stampDistanceCarry = agent.stampDistanceCarry ?? 0;

                    if (stampSpacingMode === "frames") {
                        stampFramesSinceLast += 1;
                        if (stampFramesSinceLast >= interval) {
                            stampFramesSinceLast = 0;
                            const bm = agent.brushMode;
                            if (bm === "text") {
                                const src = agent.brushTextSource ?? "?";
                                const picked = pickNextBrushTextChar(
                                    src,
                                    nextBrushTextIndex,
                                );
                                nextBrushTextIndex = picked.nextIndex;
                                stamps = [
                                    ...stamps,
                                    createTextStamp(
                                        nx,
                                        ny,
                                        liveW,
                                        stampScale,
                                        picked.char,
                                        vx,
                                        vy,
                                    ),
                                ];
                            } else {
                                stamps = [
                                    ...stamps,
                                    createBrushStamp(
                                        bm === "circles"
                                            ? "circles"
                                            : "polygon",
                                        nx,
                                        ny,
                                        liveW,
                                        polySides,
                                        stampScale,
                                    ),
                                ];
                            }
                        }
                    } else {
                        stampFramesSinceLast = 0;
                        const { positions, carryNext } =
                            stampPositionsAlongSegment(
                                distPx,
                                stampDistanceCarry,
                                agent.x,
                                agent.y,
                                nx,
                                ny,
                            );
                        stampDistanceCarry = carryNext;
                        const bm = agent.brushMode;
                        for (const p of positions) {
                            if (bm === "text") {
                                const src = agent.brushTextSource ?? "?";
                                const picked = pickNextBrushTextChar(
                                    src,
                                    nextBrushTextIndex,
                                );
                                nextBrushTextIndex = picked.nextIndex;
                                stamps = [
                                    ...stamps,
                                    createTextStamp(
                                        p.x,
                                        p.y,
                                        liveW,
                                        stampScale,
                                        picked.char,
                                        vx,
                                        vy,
                                    ),
                                ];
                            } else {
                                stamps = [
                                    ...stamps,
                                    createBrushStamp(
                                        bm === "circles"
                                            ? "circles"
                                            : "polygon",
                                        p.x,
                                        p.y,
                                        liveW,
                                        polySides,
                                        stampScale,
                                    ),
                                ];
                            }
                        }
                    }
                    const { nextSeconds: nextChildTSt, shouldSpawn: spawnSt } =
                        advancePeriodicSpawnTimer(
                            agent.childSpawnTimerSec,
                            dt,
                            sci,
                            scr,
                        );
                    nextAgents.push({
                        ...agent,
                        x: nx,
                        y: ny,
                        vx,
                        vy,
                        liveStrokeWidth: liveW,
                        points: [{ x: nx, y: ny }],
                        strokeWidths: [liveW],
                        stamps,
                        stampFramesSinceLast,
                        stampDistanceCarry:
                            stampSpacingMode === "distance"
                                ? stampDistanceCarry
                                : undefined,
                        brushTextIndex:
                            agent.brushMode === "text"
                                ? nextBrushTextIndex
                                : agent.brushTextIndex,
                        spiralArcLength: 0,
                        trailArcLength: 0,
                        childSpawnTimerSec: nextChildTSt,
                        kickBlendRemaining: nextKickRem,
                        kickPerFrameVx: nextKickPx,
                        kickPerFrameVy: nextKickPy,
                        strokeWidthBlendRemaining: nextSwRem,
                        strokeWidthPerFrameW: nextSwPw,
                    });
                    if (spawnSt) {
                        childSpawns.push(
                            ...makePathChildAgents(
                                agent,
                                nx,
                                ny,
                                vx,
                                vy,
                                liveW,
                                undefined,
                            ),
                        );
                    }
                }
            }

            const nextById = new Map(nextAgents.map((a) => [a.id, a]));

            for (const agent of agentsRef.current) {
                if (
                    !agent.parallelStrokeLeaderId ||
                    agent.brushMode !== "stroke"
                ) {
                    continue;
                }
                const leader = nextById.get(agent.parallelStrokeLeaderId);
                if (!leader) {
                    continue;
                }
                const slotT =
                    (agent.parallelStrokeSlotIndex ?? 0) -
                    (strokeLayoutK - 1) / 2;
                const { nx: pnx, ny: pny } = unitPerpendicularToMotion(
                    null,
                    leader.vx,
                    leader.vy,
                );
                const px = leader.x + pnx * strokeLayoutSpacingPx * slotT;
                const py = leader.y + pny * strokeLayoutSpacingPx * slotT;

                if (stopAtEdge && isOutsideOrOnEdge(px, py, bounds)) {
                    if (agent.points.length >= 2) {
                        finished.push({
                            id: agent.id,
                            spawnGroupId: agent.spawnGroupId,
                            brushMode: "stroke",
                            points: agent.points,
                            strokeWidths:
                                agent.strokeWidths.length ===
                                agent.points.length
                                    ? agent.strokeWidths
                                    : undefined,
                            strokeColor: agent.strokeColor,
                        });
                    }
                    continue;
                }

                const newTrailArc = leader.trailArcLength;
                if (newTrailArc >= agent.trailLengthCap - 1e-6) {
                    finished.push({
                        id: agent.id,
                        spawnGroupId: agent.spawnGroupId,
                        brushMode: "stroke",
                        points: [...agent.points, { x: px, y: py }],
                        strokeWidths:
                            agent.strokeWidths.length === agent.points.length
                                ? [
                                      ...agent.strokeWidths,
                                      leader.liveStrokeWidth,
                                  ]
                                : undefined,
                        strokeColor: agent.strokeColor,
                    });
                    continue;
                }

                nextAgents.push({
                    ...agent,
                    x: px,
                    y: py,
                    vx: leader.vx,
                    vy: leader.vy,
                    liveStrokeWidth: leader.liveStrokeWidth,
                    points: [...agent.points, { x: px, y: py }],
                    strokeWidths: [
                        ...agent.strokeWidths,
                        leader.liveStrokeWidth,
                    ],
                    trailArcLength: newTrailArc,
                    spiralArcLength: 0,
                    kickBlendRemaining: leader.kickBlendRemaining,
                    kickPerFrameVx: leader.kickPerFrameVx,
                    kickPerFrameVy: leader.kickPerFrameVy,
                    strokeWidthBlendRemaining: leader.strokeWidthBlendRemaining,
                    strokeWidthPerFrameW: leader.strokeWidthPerFrameW,
                    childSpawnTimerSec: leader.childSpawnTimerSec,
                });
            }

            if (finished.length > 0) {
                setCompletedPaths((prev) => [...prev, ...finished]);
            }

            agentsRef.current = repairGroupsAfterLeaderLoss([
                ...nextAgents,
                ...childSpawns,
            ]);
            if (
                nextAgents.length > 0 ||
                finished.length > 0 ||
                childSpawns.length > 0
            ) {
                bump();
            }
            raf = requestAnimationFrame(loop);
        };

        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [bounds, bump]);

    const addAgentAt = useCallback(
        (
            x: number,
            y: number,
            initialDir?: { dx: number; dy: number } | null,
            options?: AddAgentAtOptions,
        ): string | undefined => {
            if (!bounds) return undefined;

            const anchorLeaderAtPointer =
                options?.anchorLeaderAtPointer === true;

            spawnGroupCounterRef.current += 1;
            const spawnGroupId = `spawn-${spawnGroupCounterRef.current}`;

            const w0 = Math.min(
                strokeWidth,
                Math.max(minStrokeWidth, initialStrokeWidth),
            );

            const pickStroke = () =>
                resolveAgentStrokeColor({
                    randomPerAgent: paramsRef.current.randomStrokeColorPerAgent,
                    paletteText: paramsRef.current.strokePaletteText,
                    primaryStrokeColor: paramsRef.current.strokeColor,
                });

            if (isOutsideOrOnEdge(x, y, bounds)) {
                idCounterRef.current += 1;
                const id = `agent-${idCounterRef.current}`;
                if (agentBrushMode === "stroke") {
                    setCompletedPaths((prev) => [
                        ...prev,
                        {
                            id,
                            spawnGroupId,
                            brushMode: "stroke",
                            points: [{ x, y }],
                            strokeWidths: [w0],
                            strokeColor: pickStroke(),
                        },
                    ]);
                } else {
                    const v0 = initialVelocityFromDir(
                        initialDir,
                        baseSpeed,
                        maxSpeed,
                    );
                    if (
                        agentBrushMode === "text" ||
                        agentBrushMode === "textWithStroke"
                    ) {
                        const src = resolveBrushTextInput(brushTextContent);
                        const initialIndex =
                            brushTextRandomStartOffset && src.length > 0
                                ? ri(0, src.length - 1)
                                : 0;
                        const { char } = pickNextBrushTextChar(
                            src,
                            initialIndex,
                        );
                        setCompletedPaths((prev) => [
                            ...prev,
                            {
                                id,
                                spawnGroupId,
                                brushMode: agentBrushMode,
                                points: [],
                                stamps: [
                                    createTextStamp(
                                        x,
                                        y,
                                        w0,
                                        brushStampSizeScale,
                                        char,
                                        v0.vx,
                                        v0.vy,
                                    ),
                                ],
                                strokeColor: pickStroke(),
                                ...(agentBrushMode === "textWithStroke"
                                    ? {
                                          points: [{ x, y }],
                                          strokeWidths: [w0],
                                      }
                                    : {}),
                            },
                        ]);
                    } else {
                        setCompletedPaths((prev) => [
                            ...prev,
                            {
                                id,
                                spawnGroupId,
                                brushMode: agentBrushMode,
                                points: [],
                                stamps: [
                                    createBrushStamp(
                                        agentBrushMode === "circles"
                                            ? "circles"
                                            : "polygon",
                                        x,
                                        y,
                                        w0,
                                        brushPolygonSides,
                                        brushStampSizeScale,
                                    ),
                                ],
                                strokeColor: pickStroke(),
                            },
                        ]);
                    }
                }
                bump();
                return spawnGroupId;
            }

            const n = Math.max(1, Math.min(32, Math.round(groupSize)));

            const brushExtrasForMode = (
                mode: AgentBrushMode,
                px: number,
                py: number,
                vx: number,
                vy: number,
            ): Pick<
                PathAgent,
                | "brushMode"
                | "stamps"
                | "stampFramesSinceLast"
                | "brushTextSource"
                | "brushTextIndex"
                | "stampDistanceCarry"
            > => {
                if (mode === "stroke") {
                    return {
                        brushMode: "stroke",
                        stamps: [],
                        stampFramesSinceLast: 0,
                    };
                }
                if (mode === "text" || mode === "textWithStroke") {
                    const src = resolveBrushTextInput(brushTextContent);
                    const initialIndex =
                        brushTextRandomStartOffset && src.length > 0
                            ? ri(0, src.length - 1)
                            : 0;
                    const { char, nextIndex } = pickNextBrushTextChar(
                        src,
                        initialIndex,
                    );
                    return {
                        brushMode: mode,
                        stamps: [
                            createTextStamp(
                                px,
                                py,
                                w0,
                                brushStampSizeScale,
                                char,
                                vx,
                                vy,
                            ),
                        ],
                        stampFramesSinceLast: 0,
                        brushTextSource: src,
                        brushTextIndex: nextIndex,
                        stampDistanceCarry: 0,
                    };
                }
                return {
                    brushMode: mode,
                    stamps: [
                        createBrushStamp(
                            mode === "circles" ? "circles" : "polygon",
                            px,
                            py,
                            w0,
                            brushPolygonSides,
                            brushStampSizeScale,
                        ),
                    ],
                    stampFramesSinceLast: 0,
                    stampDistanceCarry: 0,
                };
            };

            const allBrushModes: AgentBrushMode[] = [
                "stroke",
                "circles",
                "polygon",
                "text",
                "textWithStroke",
            ];
            const randomBrushMode = (): AgentBrushMode =>
                allBrushModes[ri(0, allBrushModes.length - 1)]!;

            const trailForMode = (
                mode: AgentBrushMode,
            ): Pick<PathAgent, "trailLengthCap" | "trailArcLength"> =>
                mode === "stroke"
                    ? {
                          trailLengthCap: sampleTrailLengthCap(
                              maxTrailLength,
                              maxTrailLengthRandomness,
                          ),
                          trailArcLength: 0,
                      }
                    : {
                          trailLengthCap: MAX_TRAIL_LENGTH_CAP,
                          trailArcLength: 0,
                      };

            const childTimerInit =
                spawnChildIntervalSec > 0
                    ? sampleKickDelay(
                          spawnChildIntervalSec,
                          spawnChildIntervalRandomnessSec,
                      )
                    : undefined;

            const layoutK =
                agentBrushMode === "stroke"
                    ? Math.max(1, Math.min(32, Math.round(strokeLayoutCount)))
                    : 1;
            const layoutSpacing = Math.max(0, strokeLayoutSpacing);
            const vForPerp = initialVelocityFromDir(
                initialDir,
                baseSpeed,
                maxSpeed,
            );
            const { nx: perpNx, ny: perpNy } = unitPerpendicularToMotion(
                initialDir,
                vForPerp.vx,
                vForPerp.vy,
            );
            const layoutMid = Math.floor((layoutK - 1) / 2);
            const offsetForLayout = (j: number) => {
                const t = j - (layoutK - 1) / 2;
                return {
                    dx: perpNx * layoutSpacing * t,
                    dy: perpNy * layoutSpacing * t,
                };
            };
            const nextAgentId = () => {
                idCounterRef.current += 1;
                return `agent-${idCounterRef.current}`;
            };

            type SlotRole =
                | { kind: "solo" }
                | { kind: "groupLeader" }
                | { kind: "groupFollower"; mainLeaderId: string };

            const pushSlotAgents = (
                out: PathAgent[],
                px: number,
                py: number,
                memberBrushMode: AgentBrushMode,
                v: { vx: number; vy: number },
                role: SlotRole,
            ): string | undefined => {
                if (layoutK === 1) {
                    const id = nextAgentId();
                    const c = clampPointInsideBounds(px, py, bounds);
                    const ax = c.x;
                    const ay = c.y;
                    let leaderId: string | undefined;
                    let isGroupLeader: boolean | undefined;
                    if (role.kind === "solo") {
                        leaderId = undefined;
                        isGroupLeader = undefined;
                    } else if (role.kind === "groupLeader") {
                        leaderId = undefined;
                        isGroupLeader = true;
                    } else {
                        leaderId = role.mainLeaderId;
                        isGroupLeader = false;
                    }
                    out.push({
                        id,
                        spawnGroupId,
                        x: ax,
                        y: ay,
                        vx: v.vx,
                        vy: v.vy,
                        points: [{ x: ax, y: ay }],
                        strokeWidths: [w0],
                        liveStrokeWidth: w0,
                        strokeColor: pickStroke(),
                        spiralArcLength: 0,
                        leaderId,
                        isGroupLeader,
                        childSpawnTimerSec: childTimerInit,
                        ...trailForMode(memberBrushMode),
                        ...brushExtrasForMode(
                            memberBrushMode,
                            ax,
                            ay,
                            v.vx,
                            v.vy,
                        ),
                    });
                    return role.kind === "groupLeader" ? id : undefined;
                }

                const slotIds: string[] = [];
                for (let j = 0; j < layoutK; j++) {
                    slotIds.push(nextAgentId());
                }
                const primaryId = slotIds[layoutMid]!;
                const slaveIds = slotIds.filter((_, idx) => idx !== layoutMid);

                for (let j = 0; j < layoutK; j++) {
                    const off = offsetForLayout(j);
                    const c = clampPointInsideBounds(
                        px + off.dx,
                        py + off.dy,
                        bounds,
                    );
                    const ax = c.x;
                    const ay = c.y;
                    const id = slotIds[j]!;
                    const isPrimary = j === layoutMid;

                    let leaderId: string | undefined;
                    let isGroupLeader: boolean | undefined;
                    let parallelStrokeLeaderId: string | undefined;
                    let parallelStrokeSlotIndex: number | undefined;
                    let parallelStrokeSlaveIds: string[] | undefined;

                    if (isPrimary) {
                        parallelStrokeSlaveIds = slaveIds;
                        if (role.kind === "solo") {
                            leaderId = undefined;
                            isGroupLeader = undefined;
                        } else if (role.kind === "groupLeader") {
                            leaderId = undefined;
                            isGroupLeader = true;
                        } else {
                            leaderId = role.mainLeaderId;
                            isGroupLeader = false;
                        }
                    } else {
                        parallelStrokeLeaderId = primaryId;
                        parallelStrokeSlotIndex = j;
                    }

                    out.push({
                        id,
                        spawnGroupId,
                        x: ax,
                        y: ay,
                        vx: v.vx,
                        vy: v.vy,
                        points: [{ x: ax, y: ay }],
                        strokeWidths: [w0],
                        liveStrokeWidth: w0,
                        strokeColor: pickStroke(),
                        spiralArcLength: 0,
                        leaderId,
                        isGroupLeader,
                        parallelStrokeLeaderId,
                        parallelStrokeSlotIndex,
                        parallelStrokeSlaveIds,
                        childSpawnTimerSec: childTimerInit,
                        ...trailForMode(memberBrushMode),
                        ...brushExtrasForMode(
                            memberBrushMode,
                            ax,
                            ay,
                            v.vx,
                            v.vy,
                        ),
                    });
                }
                return role.kind === "groupLeader" ? primaryId : undefined;
            };

            if (n === 1) {
                const newOnes: PathAgent[] = [];
                const v = initialVelocityFromDir(
                    initialDir,
                    baseSpeed,
                    maxSpeed,
                );
                pushSlotAgents(newOnes, x, y, agentBrushMode, v, {
                    kind: "solo",
                });
                agentsRef.current = [...agentsRef.current, ...newOnes];
                bump();
                return spawnGroupId;
            }

            const newOnes: PathAgent[] = [];

            if (anchorLeaderAtPointer) {
                const followerPos: { x: number; y: number }[] = [];
                let guard = 0;
                while (followerPos.length < n - 1 && guard++ < 400) {
                    let p = randomPointInDisk(x, y, groupRadius);
                    p = clampPointInsideBounds(p.x, p.y, bounds);
                    if (!isOutsideOrOnEdge(p.x, p.y, bounds)) {
                        followerPos.push(p);
                    }
                }
                while (followerPos.length < n - 1) {
                    followerPos.push(clampPointInsideBounds(x, y, bounds));
                }

                const vLead = initialVelocityFromDir(
                    initialDir,
                    baseSpeed,
                    maxSpeed,
                );
                const memberBrushMode0 = groupVariedBrushes
                    ? randomBrushMode()
                    : agentBrushMode;
                const mainLeaderParallelId = pushSlotAgents(
                    newOnes,
                    x,
                    y,
                    memberBrushMode0,
                    vLead,
                    { kind: "groupLeader" },
                )!;

                for (let i = 1; i < n; i++) {
                    const pos = followerPos[i - 1]!;
                    const v = initialVelocityFromDir(
                        initialDir,
                        baseSpeed,
                        maxSpeed,
                    );
                    const memberBrushMode = groupVariedBrushes
                        ? randomBrushMode()
                        : agentBrushMode;
                    pushSlotAgents(newOnes, pos.x, pos.y, memberBrushMode, v, {
                        kind: "groupFollower",
                        mainLeaderId: mainLeaderParallelId,
                    });
                }
            } else {
                const positions: { x: number; y: number }[] = [];
                let guard = 0;
                while (positions.length < n && guard++ < 400) {
                    let p = randomPointInDisk(x, y, groupRadius);
                    p = clampPointInsideBounds(p.x, p.y, bounds);
                    if (!isOutsideOrOnEdge(p.x, p.y, bounds)) {
                        positions.push(p);
                    }
                }
                while (positions.length < n) {
                    positions.push(clampPointInsideBounds(x, y, bounds));
                }

                const leaderIdx = ri(0, n - 1);
                const slotOrder = [
                    leaderIdx,
                    ...Array.from({ length: n }, (_, i) => i).filter(
                        (i) => i !== leaderIdx,
                    ),
                ];

                let mainLeaderParallelId: string | undefined;
                for (const slotIdx of slotOrder) {
                    const pos = positions[slotIdx]!;
                    const v = initialVelocityFromDir(
                        initialDir,
                        baseSpeed,
                        maxSpeed,
                    );
                    const memberBrushMode = groupVariedBrushes
                        ? randomBrushMode()
                        : agentBrushMode;
                    if (slotIdx === leaderIdx) {
                        mainLeaderParallelId = pushSlotAgents(
                            newOnes,
                            pos.x,
                            pos.y,
                            memberBrushMode,
                            v,
                            { kind: "groupLeader" },
                        );
                    } else {
                        pushSlotAgents(
                            newOnes,
                            pos.x,
                            pos.y,
                            memberBrushMode,
                            v,
                            {
                                kind: "groupFollower",
                                mainLeaderId: mainLeaderParallelId!,
                            },
                        );
                    }
                }
            }

            agentsRef.current = [...agentsRef.current, ...newOnes];
            bump();
            return spawnGroupId;
        },
        [
            bounds,
            baseSpeed,
            maxSpeed,
            bump,
            strokeWidth,
            minStrokeWidth,
            initialStrokeWidth,
            groupSize,
            groupRadius,
            groupVariedBrushes,
            agentBrushMode,
            brushPolygonSides,
            brushStampSizeScale,
            maxTrailLength,
            maxTrailLengthRandomness,
            spawnChildIntervalSec,
            spawnChildIntervalRandomnessSec,
            strokeLayoutCount,
            strokeLayoutSpacing,
            brushTextContent,
        ],
    );

    const clearSpawnGuide = useCallback(() => {
        setSpawnGuideSvg(null);
        setSpawnGuideError(null);
    }, []);

    const spawnAgentsFromImportedGuide = useCallback((): number => {
        setSpawnGuideError(null);
        if (!spawnGuideSvg) {
            setSpawnGuideError("Import an SVG guide first.");
            return 0;
        }
        if (!bounds) {
            setSpawnGuideError("Artboard is not ready.");
            return 0;
        }
        const dist = Math.max(1, Math.round(spawnGuideSamplePx));
        const cap = Math.max(1, Math.min(5000, Math.round(spawnGuideMaxAgents)));
        let samples: ReturnType<typeof collectSpawnSamplesFromSvg>;
        try {
            samples = collectSpawnSamplesFromSvg(
                spawnGuideSvg.content,
                dist,
                cap,
                VIEW_BOX_ATTR,
            );
        } catch {
            setSpawnGuideError("Could not parse guide SVG.");
            return 0;
        }
        if (samples.length === 0) {
            setSpawnGuideError(
                "No path geometry found (paths, polylines, polygons, lines, rects).",
            );
            return 0;
        }
        for (const s of samples) {
            const dir =
                spawnGuideAlignTangent && Number.isFinite(s.angle)
                    ? { dx: Math.cos(s.angle), dy: Math.sin(s.angle) }
                    : null;
            addAgentAt(s.x, s.y, dir);
        }
        return samples.length;
    }, [
        spawnGuideSvg,
        spawnGuideSamplePx,
        spawnGuideMaxAgents,
        spawnGuideAlignTangent,
        bounds,
        addAgentAt,
    ]);

    const clearPaths = useCallback(() => {
        puppetBrushRef.current = null;
        agentsRef.current = [];
        setCompletedPaths([]);
        bump();
    }, [bump]);

    const activeAgents = agentsRef.current;

    const canExport = useMemo(
        () =>
            completedPaths.length > 0 ||
            activeAgents.some((a) =>
                a.brushMode === "stroke"
                    ? a.points.length > 0
                    : a.brushMode === "textWithStroke"
                      ? a.points.length > 0 || (a.stamps?.length ?? 0) > 0
                      : (a.stamps?.length ?? 0) > 0,
            ),
        [completedPaths, activeAgents, tick],
    );

    const buildExportSvgString = useCallback(() => {
        const fragmentsForCompleted = (c: (typeof completedPaths)[number]) => {
            const mode = c.brushMode ?? "stroke";
            const col =
                mode === "textWithStroke"
                    ? brushTextStrokeUnderlayColor
                    : (c.strokeColor ?? strokeColor);
            if (mode === "stroke" || mode === "textWithStroke") {
                const widthScale =
                    mode === "textWithStroke"
                        ? Math.max(1, brushTextStrokeUnderlayScale)
                        : 1;
                const scaledStrokeWidths = c.strokeWidths?.map(
                    (w) => w * widthScale,
                );
                return trailToSvgFragments(
                    c.points,
                    scaledStrokeWidths,
                    smoothPaths,
                    col,
                    strokeWidth * widthScale,
                    mergeTrailToSinglePath,
                    strokeDashArray,
                    filledVariableWidthStroke,
                    trailTaperStartLength,
                    trailTaperEndLength,
                );
            }
            return stampsToSvgFragments(c.stamps ?? [], col);
        };
        const fragmentsForActive = (a: PathAgent) => {
            const col =
                a.brushMode === "textWithStroke"
                    ? brushTextStrokeUnderlayColor
                    : (a.strokeColor ?? strokeColor);
            if (a.brushMode === "stroke" || a.brushMode === "textWithStroke") {
                const widthScale =
                    a.brushMode === "textWithStroke"
                        ? Math.max(1, brushTextStrokeUnderlayScale)
                        : 1;
                const scaledStrokeWidths = a.strokeWidths.map(
                    (w) => w * widthScale,
                );
                return trailToSvgFragments(
                    a.points,
                    scaledStrokeWidths,
                    smoothPaths,
                    col,
                    strokeWidth * widthScale,
                    mergeTrailToSinglePath,
                    strokeDashArray,
                    filledVariableWidthStroke,
                    trailTaperStartLength,
                    trailTaperEndLength,
                );
            }
            return stampsToSvgFragments(a.stamps ?? [], col);
        };

        const wrapTextAgentFrags = (
            agentId: string,
            mode: AgentBrushMode,
            frags: string[],
        ): string => {
            const joined = frags.join("\n    ");
            if (!exportGroupTextByAgent || frags.length === 0) {
                return joined;
            }
            if (mode !== "text" && mode !== "textWithStroke") return joined;
            const idAttr = escapeSvgDataAttr(agentId);
            return `<g class="path-agents-text-agent" data-agent-id="${idAttr}">\n    ${joined}\n  </g>`;
        };

        const activeGroupIds = new Set(
            agentsRef.current.map((a) => a.spawnGroupId),
        );
        const unionedByGroup = new Map<
            string,
            { pathD: string; fill: string }
        >();
        if (unionGroupOnComplete) {
            const trailsByGroup = new Map<
                string,
                Array<{
                    points: { x: number; y: number }[];
                    strokeWidths?: number[];
                    strokeColor: string;
                }>
            >();
            const fallbackWidthByGroup = new Map<string, number>();

            for (const c of completedPaths) {
                if (!c.spawnGroupId || activeGroupIds.has(c.spawnGroupId))
                    continue;
                const mode = c.brushMode ?? "stroke";
                if (mode !== "stroke" && mode !== "textWithStroke") continue;
                if (c.points.length < 2) continue;

                const widthScale =
                    mode === "textWithStroke"
                        ? Math.max(1, brushTextStrokeUnderlayScale)
                        : 1;
                const scaledStrokeWidths = c.strokeWidths?.map(
                    (w) => w * widthScale,
                );
                const trailColor =
                    mode === "textWithStroke"
                        ? brushTextStrokeUnderlayColor
                        : (c.strokeColor ?? strokeColor);
                const groupTrails = trailsByGroup.get(c.spawnGroupId) ?? [];
                groupTrails.push({
                    points: c.points,
                    strokeWidths: scaledStrokeWidths,
                    strokeColor: trailColor,
                });
                trailsByGroup.set(c.spawnGroupId, groupTrails);
                fallbackWidthByGroup.set(
                    c.spawnGroupId,
                    Math.max(
                        fallbackWidthByGroup.get(c.spawnGroupId) ?? 0,
                        strokeWidth * widthScale,
                    ),
                );
            }

            for (const [spawnGroupId, trails] of trailsByGroup) {
                const unioned = buildFlowAgentsGroupUnion({
                    trails,
                    fallbackStrokeWidth: Math.max(
                        1e-6,
                        fallbackWidthByGroup.get(spawnGroupId) ?? strokeWidth,
                    ),
                    mergeTrailToSinglePath,
                    filledVariableWidthStroke,
                    trailTaperStartLength,
                    trailTaperEndLength,
                });
                if (!unioned) continue;
                unionedByGroup.set(spawnGroupId, {
                    pathD: unioned.pathD,
                    fill: unioned.fill,
                });
            }
        }

        const completedClusters =
            clusterConsecutiveBySpawnGroupId(completedPaths);
        const completedBlocks = completedClusters.map((cl) => {
            const unioned = unionedByGroup.get(cl.spawnGroupId);
            const inner = cl.items
                .map((c) => {
                    const mode = c.brushMode ?? "stroke";
                    const trailFrags =
                        unioned &&
                        (mode === "stroke" || mode === "textWithStroke")
                            ? []
                            : fragmentsForCompleted(c);
                    const frags = [
                        ...trailFrags,
                        ...(mode === "textWithStroke"
                            ? stampsToSvgFragments(
                                  c.stamps ?? [],
                                  c.strokeColor ?? strokeColor,
                              )
                            : []),
                    ];
                    return wrapTextAgentFrags(c.id, mode, frags);
                })
                .filter((s) => s.length > 0)
                .join("\n    ");
            const unionFrag = unioned
                ? `<path d="${unioned.pathD}" fill="${unioned.fill}" fill-rule="nonzero" stroke="none" />`
                : "";
            const blockInner = [unionFrag, inner]
                .filter((s) => s.length > 0)
                .join("\n    ");
            return `  <g class="path-agents-spawn-group" data-spawn-group="${cl.spawnGroupId}">\n    ${blockInner}\n  </g>`;
        });

        const activeClusters = clusterConsecutiveBySpawnGroupId(
            agentsRef.current,
        );
        const activeBlocks = activeClusters.map((cl) => {
            const inner = cl.items
                .map((a) =>
                    wrapTextAgentFrags(a.id, a.brushMode, [
                        ...fragmentsForActive(a),
                        ...(a.brushMode === "textWithStroke"
                            ? stampsToSvgFragments(
                                  a.stamps ?? [],
                                  a.strokeColor ?? strokeColor,
                              )
                            : []),
                    ]),
                )
                .filter((s) => s.length > 0)
                .join("\n    ");
            return `  <g class="path-agents-spawn-group" data-spawn-group="${cl.spawnGroupId}">\n    ${inner}\n  </g>`;
        });

        const paths = [...completedBlocks, ...activeBlocks];
        const bgRect = exportBackground
            ? `  <rect width="${width}" height="${height}" fill="${backgroundColor}" />\n`
            : "";
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${VIEW_BOX_ATTR}">
${bgRect}${paths.join("\n  ")}
</svg>`;
    }, [
        completedPaths,
        smoothPaths,
        mergeTrailToSinglePath,
        unionGroupOnComplete,
        filledVariableWidthStroke,
        trailTaperStartLength,
        trailTaperEndLength,
        strokeDashArray,
        strokeColor,
        strokeWidth,
        brushTextStrokeUnderlayColor,
        brushTextStrokeUnderlayScale,
        backgroundColor,
        exportBackground,
        exportGroupTextByAgent,
        width,
        height,
        tick,
    ]);

    const handleDownload = useCallback(() => {
        const svg = buildExportSvgString();
        const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "path-agents.svg";
        a.click();
        URL.revokeObjectURL(url);
    }, [buildExportSvgString]);

    const randomizeParameters = useCallback(() => {
        setKickIntervalSec(Math.max(0.25, Math.round(rf(0.5, 10) * 4) / 4));
        setKickIntervalRandomnessSec(Math.round(rf(0, 4) * 100) / 100);
        setForceMagnitude(Math.round(rf(10, 900) / 5) * 5);

        const baseSp = Math.round(rf(60, 900) / 5) * 5;
        setBaseSpeed(Math.max(1, baseSp));
        const maxSp = Math.round(rf(Math.max(baseSp, 120), 2800) / 10) * 10;
        setMaxSpeed(Math.max(baseSp, maxSp));

        const dirLockModes: DirectionLockMode[] = ["none", "deg45", "deg90"];
        setDirectionLock(
            Math.random() < 0.7
                ? "none"
                : dirLockModes[ri(1, dirLockModes.length - 1)]!,
        );

        setMaxTrailLength(ri(8, 80) * 100);
        setMaxTrailLengthRandomness(Math.round(rf(0, 0.85) * 100) / 100);

        setSpawnChildIntervalSec(
            Math.random() < 0.55 ? 0 : Math.round(rf(0.25, 3.5) * 20) / 20,
        );
        setSpawnChildIntervalRandomnessSec(Math.round(rf(0, 1.2) * 20) / 20);
        const capModes: ChildTrailCapMode[] = [
            "none",
            "parentRemaining",
            "fixed",
        ];
        setChildTrailCapMode(capModes[ri(0, capModes.length - 1)]!);
        setChildTrailCapFixed(ri(400, 4800));
        setChildTrailCapRandomness(Math.round(rf(0, 0.85) * 100) / 100);

        setGravity(Math.round(rf(-420, 420) / 5) * 5);

        const minW = Math.round(rf(0.05, 3) * 100) / 100;
        const maxW = Math.min(
            40,
            Math.max(minW + 0.25, Math.round(rf(minW + 0.2, 22) * 100) / 100),
        );
        setMinStrokeWidth(minW);
        setStrokeWidth(maxW);
        setInitialStrokeWidth(Math.round(rf(minW, maxW) * 100) / 100);

        setStrokeWidthStep(Math.round(rf(0, 5) * 100) / 100);
        setStrokeWidthKickFrames(ri(0, 120));
        setStrokeWidthKickGradual(Math.random() < 0.35);
        setStrokeWidthBlendFrames(ri(4, 28));

        setSmoothPaths(Math.random() < 0.42);
        setFilledVariableWidthStroke(Math.random() < 0.22);
        setVelocityKickGradual(Math.random() < 0.32);
        setVelocityKickBlendFrames(ri(4, 24));

        setGroupSize(ri(1, 8));
        setGroupRadius(Math.round(rf(18, 140)));
        setStrokeLayoutCount(ri(1, 6));
        setStrokeLayoutSpacing(Math.round(rf(4, 36)));
        setGroupCohesionStrength(Math.round(rf(2, 16) * 10) / 10);
        setGroupVariedBrushes(Math.random() < 0.72);

        const brushModes: AgentBrushMode[] = [
            "stroke",
            "circles",
            "polygon",
            "text",
        ];
        setAgentBrushMode(brushModes[ri(0, brushModes.length - 1)]!);
        setBrushStampIntervalFrames(ri(3, 28));
        setBrushPolygonSides(ri(3, 10));
        setBrushStampSizeScale(Math.round(rf(0.75, 2.6) * 10) / 10);
        setBrushStampSpacingMode(Math.random() < 0.55 ? "frames" : "distance");
        setBrushStampDistancePx(ri(10, 72));

        setSpiralTrail(Math.random() < 0.38);
        setSpiralAmplitude(Math.round(rf(6, 32)));
        setSpiralWavelength(Math.round(rf(48, 140)));
        setSpiralGrowthPer1k(Math.round(rf(0, 0.42) * 100) / 100);

        setStrokeColor(
            `#${hexByte(90, 255)}${hexByte(90, 255)}${hexByte(90, 255)}`,
        );
        setBackgroundColor(
            `#${hexByte(4, 42)}${hexByte(4, 42)}${hexByte(6, 48)}`,
        );
    }, []);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            kickIntervalSec,
            kickIntervalRandomnessSec,
            velocityKickGradual,
            velocityKickBlendFrames,
            forceMagnitude,
            baseSpeed,
            maxSpeed,
            directionLock,
            maxTrailLength,
            maxTrailLengthRandomness,
            stopAtMargin,
            spawnChildIntervalSec,
            spawnChildIntervalRandomnessSec,
            childTrailCapMode,
            childTrailCapFixed,
            childTrailCapRandomness,
            gravity,
            strokeColor,
            strokePaletteText,
            randomStrokeColorPerAgent,
            strokeWidth,
            minStrokeWidth,
            initialStrokeWidth,
            strokeWidthStep,
            strokeWidthKickFrames,
            strokeWidthKickGradual,
            strokeWidthBlendFrames,
            backgroundColor,
            exportBackground,
            smoothPaths,
            mergeTrailToSinglePath,
            unionGroupOnComplete,
            filledVariableWidthStroke,
            trailTaperStartLength,
            trailTaperEndLength,
            dashedStroke,
            strokeDashLength,
            strokeDashGap,
            groupSize,
            groupRadius,
            strokeLayoutCount,
            strokeLayoutSpacing,
            groupCohesionStrength,
            groupVariedBrushes,
            agentBrushMode,
            brushStampIntervalFrames,
            brushPolygonSides,
            brushStampSizeScale,
            brushStampSpacingMode,
            brushStampDistancePx,
            brushTextContent,
            brushTextStrokeUnderlayColor,
            brushTextStrokeUnderlayScale,
            brushTextRandomStartOffset,
            exportGroupTextByAgent,
            spiralTrail,
            spiralAmplitude,
            spiralWavelength,
            spiralGrowthPer1k,
            inputMode,
        }),
        [
            kickIntervalSec,
            kickIntervalRandomnessSec,
            velocityKickGradual,
            velocityKickBlendFrames,
            forceMagnitude,
            baseSpeed,
            maxSpeed,
            directionLock,
            maxTrailLength,
            maxTrailLengthRandomness,
            stopAtMargin,
            spawnChildIntervalSec,
            spawnChildIntervalRandomnessSec,
            childTrailCapMode,
            childTrailCapFixed,
            childTrailCapRandomness,
            gravity,
            strokeColor,
            strokePaletteText,
            randomStrokeColorPerAgent,
            strokeWidth,
            minStrokeWidth,
            initialStrokeWidth,
            strokeWidthStep,
            strokeWidthKickFrames,
            strokeWidthKickGradual,
            strokeWidthBlendFrames,
            backgroundColor,
            exportBackground,
            smoothPaths,
            mergeTrailToSinglePath,
            unionGroupOnComplete,
            filledVariableWidthStroke,
            trailTaperStartLength,
            trailTaperEndLength,
            dashedStroke,
            strokeDashLength,
            strokeDashGap,
            groupSize,
            groupRadius,
            strokeLayoutCount,
            strokeLayoutSpacing,
            groupCohesionStrength,
            groupVariedBrushes,
            agentBrushMode,
            brushStampIntervalFrames,
            brushPolygonSides,
            brushStampSizeScale,
            brushStampSpacingMode,
            brushStampDistancePx,
            brushTextContent,
            brushTextStrokeUnderlayColor,
            brushTextStrokeUnderlayScale,
            brushTextRandomStartOffset,
            exportGroupTextByAgent,
            spiralTrail,
            spiralAmplitude,
            spiralWavelength,
            spiralGrowthPer1k,
            inputMode,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.kickIntervalSec != null)
            setKickIntervalSec(values.kickIntervalSec as number);
        if (values.kickIntervalRandomnessSec != null)
            setKickIntervalRandomnessSec(
                values.kickIntervalRandomnessSec as number,
            );
        if (values.velocityKickGradual != null)
            setVelocityKickGradual(values.velocityKickGradual as boolean);
        if (values.velocityKickBlendFrames != null)
            setVelocityKickBlendFrames(
                values.velocityKickBlendFrames as number,
            );
        if (values.forceMagnitude != null)
            setForceMagnitude(values.forceMagnitude as number);
        if (values.baseSpeed != null) setBaseSpeed(values.baseSpeed as number);
        if (values.maxSpeed != null) setMaxSpeed(values.maxSpeed as number);
        if (
            values.directionLock === "none" ||
            values.directionLock === "deg45" ||
            values.directionLock === "deg90"
        ) {
            setDirectionLock(values.directionLock);
        }
        if (values.maxTrailLength != null)
            setMaxTrailLength(values.maxTrailLength as number);
        if (values.maxTrailLengthRandomness != null)
            setMaxTrailLengthRandomness(
                values.maxTrailLengthRandomness as number,
            );
        if (values.stopAtMargin != null)
            setStopAtMargin(values.stopAtMargin as boolean);
        if (values.spawnChildIntervalSec != null)
            setSpawnChildIntervalSec(values.spawnChildIntervalSec as number);
        if (values.spawnChildIntervalRandomnessSec != null)
            setSpawnChildIntervalRandomnessSec(
                values.spawnChildIntervalRandomnessSec as number,
            );
        if (
            values.childTrailCapMode === "none" ||
            values.childTrailCapMode === "parentRemaining" ||
            values.childTrailCapMode === "fixed"
        ) {
            setChildTrailCapMode(values.childTrailCapMode);
        } else if (values.childTrailCapFromParentRemaining === true) {
            setChildTrailCapMode("parentRemaining");
        } else if (values.childTrailCapFromParentRemaining === false) {
            setChildTrailCapMode("none");
        }
        if (values.childTrailCapFixed != null) {
            setChildTrailCapFixed(values.childTrailCapFixed as number);
        }
        if (values.childTrailCapRandomness != null) {
            setChildTrailCapRandomness(
                values.childTrailCapRandomness as number,
            );
        }
        if (values.gravity != null) setGravity(values.gravity as number);
        if (values.strokeColor != null)
            setStrokeColor(values.strokeColor as string);
        if (values.strokePaletteText != null) {
            setStrokePaletteText(values.strokePaletteText as string);
        }
        if (values.randomStrokeColorPerAgent != null) {
            setRandomStrokeColorPerAgent(
                values.randomStrokeColorPerAgent as boolean,
            );
        }
        if (values.strokeWidth != null)
            setStrokeWidth(values.strokeWidth as number);
        if (values.minStrokeWidth != null)
            setMinStrokeWidth(values.minStrokeWidth as number);
        if (values.initialStrokeWidth != null)
            setInitialStrokeWidth(values.initialStrokeWidth as number);
        if (values.strokeWidthStep != null)
            setStrokeWidthStep(values.strokeWidthStep as number);
        if (values.strokeWidthKickFrames != null)
            setStrokeWidthKickFrames(values.strokeWidthKickFrames as number);
        if (values.strokeWidthKickGradual != null)
            setStrokeWidthKickGradual(values.strokeWidthKickGradual as boolean);
        if (values.strokeWidthBlendFrames != null)
            setStrokeWidthBlendFrames(values.strokeWidthBlendFrames as number);
        if (values.backgroundColor != null)
            setBackgroundColor(values.backgroundColor as string);
        if (values.exportBackground != null)
            setExportBackground(values.exportBackground as boolean);
        if (values.smoothPaths != null)
            setSmoothPaths(values.smoothPaths as boolean);
        if (values.mergeTrailToSinglePath != null)
            setMergeTrailToSinglePath(values.mergeTrailToSinglePath as boolean);
        if (values.unionGroupOnComplete != null)
            setUnionGroupOnComplete(values.unionGroupOnComplete as boolean);
        if (values.filledVariableWidthStroke != null)
            setFilledVariableWidthStroke(
                values.filledVariableWidthStroke as boolean,
            );
        if (values.trailTaperStartLength != null)
            setTrailTaperStartLength(values.trailTaperStartLength as number);
        if (values.trailTaperEndLength != null)
            setTrailTaperEndLength(values.trailTaperEndLength as number);
        if (values.dashedStroke != null)
            setDashedStroke(values.dashedStroke as boolean);
        if (values.strokeDashLength != null)
            setStrokeDashLength(values.strokeDashLength as number);
        if (values.strokeDashGap != null)
            setStrokeDashGap(values.strokeDashGap as number);
        if (values.groupSize != null) setGroupSize(values.groupSize as number);
        if (values.groupRadius != null)
            setGroupRadius(values.groupRadius as number);
        if (values.strokeLayoutCount != null)
            setStrokeLayoutCount(values.strokeLayoutCount as number);
        if (values.strokeLayoutSpacing != null)
            setStrokeLayoutSpacing(values.strokeLayoutSpacing as number);
        if (values.groupCohesionStrength != null)
            setGroupCohesionStrength(values.groupCohesionStrength as number);
        if (values.groupVariedBrushes != null)
            setGroupVariedBrushes(values.groupVariedBrushes as boolean);
        if (values.agentBrushMode != null)
            setAgentBrushMode(values.agentBrushMode as AgentBrushMode);
        if (values.brushStampIntervalFrames != null)
            setBrushStampIntervalFrames(
                values.brushStampIntervalFrames as number,
            );
        if (values.brushPolygonSides != null)
            setBrushPolygonSides(values.brushPolygonSides as number);
        if (values.brushStampSizeScale != null)
            setBrushStampSizeScale(values.brushStampSizeScale as number);
        if (
            values.brushStampSpacingMode === "frames" ||
            values.brushStampSpacingMode === "distance"
        ) {
            setBrushStampSpacingMode(values.brushStampSpacingMode);
        }
        if (values.brushStampDistancePx != null) {
            setBrushStampDistancePx(values.brushStampDistancePx as number);
        }
        if (values.brushTextContent != null) {
            setBrushTextContent(values.brushTextContent as string);
        }
        if (values.brushTextStrokeUnderlayColor != null) {
            setBrushTextStrokeUnderlayColor(
                values.brushTextStrokeUnderlayColor as string,
            );
        }
        if (values.brushTextStrokeUnderlayScale != null) {
            setBrushTextStrokeUnderlayScale(
                values.brushTextStrokeUnderlayScale as number,
            );
        }
        if (values.brushTextRandomStartOffset != null) {
            setBrushTextRandomStartOffset(
                values.brushTextRandomStartOffset as boolean,
            );
        }
        if (values.exportGroupTextByAgent != null) {
            setExportGroupTextByAgent(values.exportGroupTextByAgent as boolean);
        }
        if (values.spiralTrail != null)
            setSpiralTrail(values.spiralTrail as boolean);
        if (values.spiralAmplitude != null)
            setSpiralAmplitude(values.spiralAmplitude as number);
        if (values.spiralWavelength != null)
            setSpiralWavelength(values.spiralWavelength as number);
        if (values.spiralGrowthPer1k != null)
            setSpiralGrowthPer1k(values.spiralGrowthPer1k as number);
        if (
            values.inputMode === "clickSpawn" ||
            values.inputMode === "dragPaint"
        ) {
            setInputMode(values.inputMode);
        }
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "path-agents",
        defaultPreset: DEFAULT_PATH_AGENTS_PRESET,
        getPresetValues,
        applyPreset,
    });

    useSvgToolHandoffReceiver(
        "path-agents",
        {
            onPath: (svg) => {
                setSpawnGuideError(null);
                setSpawnGuideSvg(svg);
            },
            onPrimary: (svg) => {
                setSpawnGuideError(null);
                setSpawnGuideSvg(svg);
            },
        },
        { setError: setSpawnGuideError },
    );

    const value = useMemo<PathAgentsState>(
        () => ({
            viewBoxAttr: VIEW_BOX_ATTR,
            width,
            height,
            bounds,
            activeAgents,
            completedPaths,
            kickIntervalSec,
            kickIntervalRandomnessSec,
            velocityKickGradual,
            velocityKickBlendFrames,
            forceMagnitude,
            baseSpeed,
            maxSpeed,
            directionLock,
            setDirectionLock,
            maxTrailLength,
            maxTrailLengthRandomness,
            stopAtMargin,
            spawnChildIntervalSec,
            spawnChildIntervalRandomnessSec,
            childTrailCapMode,
            childTrailCapFixed,
            childTrailCapRandomness,
            gravity,
            strokeColor,
            strokePaletteText,
            setStrokePaletteText,
            randomStrokeColorPerAgent,
            setRandomStrokeColorPerAgent,
            strokeWidth,
            minStrokeWidth,
            initialStrokeWidth,
            strokeWidthStep,
            strokeWidthKickFrames,
            strokeWidthKickGradual,
            strokeWidthBlendFrames,
            backgroundColor,
            exportBackground,
            smoothPaths,
            setSmoothPaths,
            mergeTrailToSinglePath,
            setMergeTrailToSinglePath,
            unionGroupOnComplete,
            setUnionGroupOnComplete,
            filledVariableWidthStroke,
            setFilledVariableWidthStroke,
            trailTaperStartLength,
            setTrailTaperStartLength,
            trailTaperEndLength,
            setTrailTaperEndLength,
            strokeDashArray,
            dashedStroke,
            setDashedStroke,
            strokeDashLength,
            setStrokeDashLength,
            strokeDashGap,
            setStrokeDashGap,
            groupSize,
            setGroupSize,
            groupRadius,
            setGroupRadius,
            strokeLayoutCount,
            setStrokeLayoutCount,
            strokeLayoutSpacing,
            setStrokeLayoutSpacing,
            groupCohesionStrength,
            setGroupCohesionStrength,
            groupVariedBrushes,
            setGroupVariedBrushes,
            agentBrushMode,
            setAgentBrushMode,
            brushStampIntervalFrames,
            setBrushStampIntervalFrames,
            brushPolygonSides,
            setBrushPolygonSides,
            brushStampSizeScale,
            setBrushStampSizeScale,
            brushStampSpacingMode,
            setBrushStampSpacingMode,
            brushStampDistancePx,
            setBrushStampDistancePx,
            brushTextContent,
            setBrushTextContent,
            brushTextStrokeUnderlayColor,
            setBrushTextStrokeUnderlayColor,
            brushTextStrokeUnderlayScale,
            setBrushTextStrokeUnderlayScale,
            brushTextRandomStartOffset,
            setBrushTextRandomStartOffset,
            exportGroupTextByAgent,
            setExportGroupTextByAgent,
            spiralTrail,
            setSpiralTrail,
            spiralAmplitude,
            setSpiralAmplitude,
            spiralWavelength,
            setSpiralWavelength,
            spiralGrowthPer1k,
            setSpiralGrowthPer1k,
            spawnGuideSvg,
            setSpawnGuideSvg,
            spawnGuideSamplePx,
            setSpawnGuideSamplePx,
            spawnGuideMaxAgents,
            setSpawnGuideMaxAgents,
            spawnGuideAlignTangent,
            setSpawnGuideAlignTangent,
            spawnGuideError,
            setSpawnGuideError,
            clearSpawnGuide,
            spawnAgentsFromImportedGuide,
            inputMode,
            setInputMode,
            setPuppetBrushTarget,
            clearPuppetBrush,
            setKickIntervalSec,
            setKickIntervalRandomnessSec,
            setVelocityKickGradual,
            setVelocityKickBlendFrames,
            setForceMagnitude,
            setBaseSpeed,
            setMaxSpeed,
            setMaxTrailLength,
            setMaxTrailLengthRandomness,
            setStopAtMargin,
            setSpawnChildIntervalSec,
            setSpawnChildIntervalRandomnessSec,
            setChildTrailCapMode,
            setChildTrailCapFixed,
            setChildTrailCapRandomness,
            setGravity,
            setStrokeColor,
            setStrokeWidth,
            setMinStrokeWidth,
            setInitialStrokeWidth,
            setStrokeWidthStep,
            setStrokeWidthKickFrames,
            setStrokeWidthKickGradual,
            setStrokeWidthBlendFrames,
            setBackgroundColor,
            setExportBackground,
            addAgentAt,
            clearPaths,
            canExport,
            buildExportSvgString,
            handleDownload,
            randomizeParameters,
            applyPreset,
            getPresetValues,
            resetParameters,
        }),
        [
            width,
            height,
            bounds,
            activeAgents,
            completedPaths,
            kickIntervalSec,
            kickIntervalRandomnessSec,
            velocityKickGradual,
            velocityKickBlendFrames,
            forceMagnitude,
            baseSpeed,
            maxSpeed,
            directionLock,
            maxTrailLength,
            maxTrailLengthRandomness,
            stopAtMargin,
            spawnChildIntervalSec,
            spawnChildIntervalRandomnessSec,
            childTrailCapMode,
            childTrailCapFixed,
            childTrailCapRandomness,
            gravity,
            strokeColor,
            strokePaletteText,
            randomStrokeColorPerAgent,
            strokeWidth,
            minStrokeWidth,
            initialStrokeWidth,
            strokeWidthStep,
            strokeWidthKickFrames,
            strokeWidthKickGradual,
            strokeWidthBlendFrames,
            backgroundColor,
            exportBackground,
            smoothPaths,
            mergeTrailToSinglePath,
            unionGroupOnComplete,
            filledVariableWidthStroke,
            trailTaperStartLength,
            trailTaperEndLength,
            strokeDashArray,
            dashedStroke,
            strokeDashLength,
            strokeDashGap,
            groupSize,
            groupRadius,
            strokeLayoutCount,
            strokeLayoutSpacing,
            groupCohesionStrength,
            groupVariedBrushes,
            agentBrushMode,
            brushStampIntervalFrames,
            brushPolygonSides,
            brushStampSizeScale,
            brushStampSpacingMode,
            brushStampDistancePx,
            brushTextContent,
            brushTextStrokeUnderlayColor,
            brushTextStrokeUnderlayScale,
            brushTextRandomStartOffset,
            exportGroupTextByAgent,
            spiralTrail,
            spiralAmplitude,
            spiralWavelength,
            spiralGrowthPer1k,
            spawnGuideSvg,
            spawnGuideSamplePx,
            spawnGuideMaxAgents,
            spawnGuideAlignTangent,
            spawnGuideError,
            clearSpawnGuide,
            spawnAgentsFromImportedGuide,
            inputMode,
            canExport,
            buildExportSvgString,
            handleDownload,
            randomizeParameters,
            resetParameters,
            tick,
            setInputMode,
            setPuppetBrushTarget,
            clearPuppetBrush,
            addAgentAt,
            setSmoothPaths,
            setMergeTrailToSinglePath,
            setFilledVariableWidthStroke,
            setTrailTaperStartLength,
            setTrailTaperEndLength,
            setDashedStroke,
            setStrokeDashLength,
            setStrokeDashGap,
            setVelocityKickGradual,
            setVelocityKickBlendFrames,
            setSpiralTrail,
            setSpiralAmplitude,
            setSpiralWavelength,
            setSpiralGrowthPer1k,
            applyPreset,
            getPresetValues,
        ],
    );

    return (
        <PathAgentsContext.Provider value={value}>
            {children}
        </PathAgentsContext.Provider>
    );
}
